import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStatusFlag } from '@/components/hooks';
import StatusFlagButtons from '@/components/common/StatusFlagButtons';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Flag, X, ZoomIn, ZoomOut, Target, Edit2, Trash2, Users, UserPlus, ChevronDown, ChevronUp, Filter, Check, Shield, Sparkles, AlertTriangle, AlertOctagon, Bot, Play, ExternalLink, Loader2, RotateCw, Cpu, Upload, FileText } from 'lucide-react';
import { Calendar } from "@/components/ui/calendar";
import ReactMarkdown from 'react-markdown';
import { format, addDays, subDays, startOfDay, isSameDay } from 'date-fns';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import DailyTaskPrinciplesValidation from '../commander/DailyTaskPrinciplesValidation';
import ArchivedParentBadge from '@/components/common/ArchivedParentBadge';
import PendingBotTasksApproval from './PendingBotTasksApproval';
import MicroTaskListDialog from './MicroTaskListDialog';
import DocumentSaveDialog from '../bots/DocumentSaveDialog';
import BotDocumentReviewDialog from '../bots/BotDocumentReviewDialog';
import BotPlanDialog from '../bots/BotPlanDialog';
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import TaskCategorizationPanel from '@/components/gamification/TaskCategorizationPanel';
import { ListTodo } from 'lucide-react';
import ArchiveConfirmDialog from '@/components/common/ArchiveConfirmDialog';
import ReportActivityIssueModal from '@/components/civilization/ReportActivityIssueModal';
import { Archive } from 'lucide-react';

// Quick To-Do button with badge showing incomplete task count
function QuickTodoButton({ officer, onClick }) {
  const { data: microTasks = [] } = useQuery({
    queryKey: ['microTasks', officer?.user_email],
    queryFn: () => base44.entities.MicroTask.filter({ user_email: officer?.user_email }),
    enabled: !!officer?.user_email
  });

  const incompleteCount = microTasks.filter(t => !t.is_completed).length;

  return (
    <Button
      size="icon"
      variant="ghost"
      onClick={onClick}
      className="h-9 w-9 bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/40 hover:text-white mr-3 transition-all duration-300 hover:scale-110 hover:shadow-[0_0_15px_rgba(99,102,241,0.6)] border border-indigo-500/40 relative"
      title="Quick To-Dos"
    >
      <ListTodo className="w-5 h-5" />
      {incompleteCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 shadow-lg">
          {incompleteCount > 99 ? '99+' : incompleteCount}
        </span>
      )}
    </Button>
  );
}

export default function DailyTaskPlanner({ user, isCommander }) {
  const queryClient = useQueryClient();
  
  // Custom flag update handler that opens issue dialog when red is selected
  const handleStatusFlagChange = async (taskId, newFlag) => {
    // Update the flag
    const task = dailyTasks.find(t => t.id === taskId);
    if (task) {
      await base44.entities.DailyTask.update(taskId, { status_flag: newFlag });
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      
      // If red flag is selected, open issue reporting dialog
      if (newFlag === 'red') {
        setIssueReportDialog({ open: true, task });
      }
    }
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [zoomLevel, setZoomLevel] = useState(window.innerWidth < 768 ? 0 : 1); // 0 = today only, 1 = yesterday/today/tomorrow, 2 = +/-3 days, 3 = +/-7 days
  const [centerDate, setCenterDate] = useState(new Date());
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && zoomLevel > 0) {
        setZoomLevel(0);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [zoomLevel]);
  const [addTaskDialog, setAddTaskDialog] = useState(false);
  const [editTaskDialog, setEditTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [squadEmails, setSquadEmails] = useState([]);
  const [squadPopoverOpen, setSquadPopoverOpen] = useState(false);
  const [collapsedOfficers, setCollapsedOfficers] = useState([]);
  const [filters, setFilters] = useState({
    missions: [],
    objectives: [],
    activities: [],
    tags: []
  });
  const [selectedTagType, setSelectedTagType] = useState('');
  const [missionPopoverOpen, setMissionPopoverOpen] = useState(false);
  const [objectivePopoverOpen, setObjectivePopoverOpen] = useState(false);
  const [activityPopoverOpen, setActivityPopoverOpen] = useState(false);
  const [tagTypePopoverOpen, setTagTypePopoverOpen] = useState(false);
  const [tagValuePopoverOpen, setTagValuePopoverOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    activity_id: '',
    task_description: '',
    key_result_focus: '',
    time_allocation_percent: 40,
    task_order: 1,
    status_flag: 'yellow',
    tags: [],
    assign_to_email: '',
    is_tentative: false,
    // Gamification fields
    category: null,
    xp_value: 0,
    ep_value: 0,
    skill_rewards: [],
    bot_suggested_category: null,
    bot_suggested_xp: null,
    bot_suggested_ep: null,
    bot_suggested_skills: null,
    bot_suggestion_approved: false
  });
  const [currentTagType, setCurrentTagType] = useState('');
  const [currentTagValue, setCurrentTagValue] = useState('');
  const [validatingTasks, setValidatingTasks] = useState([]);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const [isGeneratingTask, setIsGeneratingTask] = useState(false);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [taskDetailDialog, setTaskDetailDialog] = useState({ open: false, task: null });
  const [executionDialog, setExecutionDialog] = useState({ open: false, taskId: null, progress: 0, completed: false });
  const [artifactApprovalDialog, setArtifactApprovalDialog] = useState({ open: false, artifacts: [], uploadedFiles: [], taskUserEmail: null });
  const [modelSelectorOpen, setModelSelectorOpen] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dialogMissionFilter, setDialogMissionFilter] = useState([]);
  const [dialogObjectiveFilter, setDialogObjectiveFilter] = useState([]);
  const [uploadingArtifact, setUploadingArtifact] = useState(false);
  const [microTaskListDialog, setMicroTaskListDialog] = useState({ open: false, officer: null });
  const [documentSaveDialog, setDocumentSaveDialog] = useState({ open: false, documentOutput: null, taskUserEmail: null });
  const [documentReviewDialog, setDocumentReviewDialog] = useState({ open: false, documentOutput: null, taskId: null, taskUserEmail: null });
  const [botPlanDialog, setBotPlanDialog] = useState({ open: false, task: null });
  const [rejectionDialog, setRejectionDialog] = useState({ open: false, task: null, reason: '' });
  const [archiveDialog, setArchiveDialog] = useState({ open: false, task: null });
  const [issueReportDialog, setIssueReportDialog] = useState({ open: false, task: null });
  const [statusFilterMode, setStatusFilterMode] = useState('all'); // 'all', 'active', 'archived'
  const [conqueredFilter, setConqueredFilter] = useState('all'); // 'all', 'conquered', 'imminent', 'struggling', 'not_started'
  
  const availableModels = [
    { value: 'claude-haiku-4-5-20251001', label: '⚡ Haiku 4.5 — Fast & Cheap ($1/$5 per MTok)', description: 'Best for routine tasks. 5x faster than Sonnet.' },
    { value: 'claude-sonnet-4-5-20250929', label: '⚖️ Sonnet 4.5 — Balanced ($3/$15 per MTok)', description: 'Best for complex reasoning and quality output.' },
    { value: 'claude-opus-4-6', label: '🧠 Opus 4.6 — Maximum Quality ($5/$25 per MTok)', description: 'Best for critical deliverables requiring deep analysis.' },
  ];
  
  const location = useLocation();

  // Handle URL parameters for navigation from other pages
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    const userParam = params.get('user');
    const taskIdParam = params.get('taskId');

    if (dateParam) {
      setCenterDate(new Date(dateParam + 'T00:00:00'));
    }

    if (userParam && userParam !== user?.email) {
      setSquadEmails(prev => {
        if (!prev.includes(userParam)) {
          return [...prev, userParam];
        }
        return prev;
      });
      // Ensure the user is expanded
      setCollapsedOfficers(prev => prev.filter(email => email !== userParam));
    }
    
    if (taskIdParam) {
      setHighlightedTaskId(taskIdParam);
      // Clear highlight after a few seconds
      setTimeout(() => setHighlightedTaskId(null), 5000);
    }
  }, [location.search, user?.email]);

  // Fetch daily tasks
  const { data: dailyTasks = [] } = useQuery({
    queryKey: ['dailyTasks', user?.email],
    queryFn: () => base44.entities.DailyTask.list(),
    enabled: !!user?.email
  });

  // Open task detail modal from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get('taskId');
    const openModalParam = params.get('openModal');
    
    if (openModalParam === 'taskDetails' && taskIdParam && dailyTasks.length > 0) {
      const task = dailyTasks.find(t => t.id === taskIdParam);
      if (task) {
        setTaskDetailDialog({ open: true, task });
      }
    }
  }, [location.search, dailyTasks]);

  // Fetch activities where user is responsible OR accountable (for AI task assignment)
  const { data: myActivities = [] } = useQuery({
    queryKey: ['myActivities', user?.email],
    queryFn: async () => {
      const activities = await base44.entities.Activity.list();
      return activities.filter((a) => a.responsible_email === user?.email || a.accountable_email === user?.email);
    },
    enabled: !!user?.email
  });

  // Fetch all officer profiles for squad selection
  const { data: officers = [] } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  // Fetch artifacts, rituals, policies, capabilities for tag selection
  const { data: artifacts = [] } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: []
  });

  const { data: rituals = [] } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list(),
    initialData: []
  });

  const { data: policies = [] } = useQuery({
    queryKey: ['policies'],
    queryFn: () => base44.entities.Policy.list(),
    initialData: []
  });

  const { data: capabilities = [] } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => base44.entities.Capability.list(),
    initialData: []
  });

  // Fetch current user's profile
  const { data: myProfile } = useQuery({
    queryKey: ['myProfile', user?.email],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user?.email
  });

  // Load squad from profile on mount
  React.useEffect(() => {
    if (myProfile?.squad_members) {
      const params = new URLSearchParams(window.location.search);
      const userParam = params.get('user');
      let newSquad = [...myProfile.squad_members];
      if (userParam && userParam !== user?.email && !newSquad.includes(userParam)) {
        newSquad.push(userParam);
      }
      setSquadEmails(newSquad);
    }
  }, [myProfile, user?.email]);

  // Fetch missions, objectives, activities for filters
  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: objectives = [] } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: allActivities = [] } = useQuery({
    queryKey: ['allActivities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  // Helper to get activity by ID (search all activities, not just user's)
  const getActivity = (activityId) => {
    return allActivities.find((a) => a.id === activityId);
  };

  // Helper to calculate level from XP
  const calculateLevel = (xp) => {
    return Math.floor(xp / 500) + 1;
  };

  // Helper to get officer by email
  const getOfficer = (email) => {
    return officers.find((o) => o.user_email === email);
  };

  // Save squad mutation
  const updateSquadMutation = useMutation({
    mutationFn: (squadMembers) => {
      if (!myProfile) return Promise.resolve();
      return base44.entities.OfficerProfile.update(myProfile.id, {
        squad_members: squadMembers
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    }
  });

  // Add squad member
  const addSquadMember = (email) => {
    if (!squadEmails.includes(email)) {
      const newSquad = [...squadEmails, email];
      setSquadEmails(newSquad);
      updateSquadMutation.mutate(newSquad);
    }
    setSquadPopoverOpen(false);
  };

  // Remove squad member
  const removeSquadMember = (email) => {
    const newSquad = squadEmails.filter((e) => e !== email);
    setSquadEmails(newSquad);
    updateSquadMutation.mutate(newSquad);
  };

  // Toggle officer collapse
  const toggleOfficerCollapse = (email) => {
    setCollapsedOfficers((prev) =>
    prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  // Check if task matches filters
  const taskMatchesFilters = (task) => {
    if (filters.activities.length === 0 && filters.objectives.length === 0 && filters.missions.length === 0 && filters.tags.length === 0) {
      return true; // No filters applied
    }

    // Check activity filter
    if (filters.activities.length > 0 && !filters.activities.includes(task.activity_id)) {
      return false;
    }

    // Check objective filter (via activity)
    if (filters.objectives.length > 0) {
      const activity = allActivities.find((a) => a.id === task.activity_id);
      if (!activity || !filters.objectives.includes(activity.objective_id)) {
        return false;
      }
    }

    // Check mission filter (via activity -> objective)
    if (filters.missions.length > 0) {
      const activity = allActivities.find((a) => a.id === task.activity_id);
      if (!activity) return false;
      const objective = objectives.find((o) => o.id === activity.objective_id);
      if (!objective || !filters.missions.includes(objective.mission_id)) {
        return false;
      }
    }

    // Check tags filter
    if (filters.tags.length > 0) {
      if (!task.tags || task.tags.length === 0) return false;
      const hasMatchingTag = task.tags.some((tag) => filters.tags.includes(tag));
      if (!hasMatchingTag) return false;
    }

    return true;
  };

  // Get all unique tags from all tasks
  const allUniqueTags = [...new Set(dailyTasks.flatMap((task) => task.tags || []))];

  // Get unique tag types - extract from format "value (Type)"
  const uniqueTagTypes = [...new Set(allUniqueTags.map((tag) => {
    const match = tag.match(/\(([^)]+)\)$/);
    return match ? match[1] : '';
  }).filter(Boolean))];

  // Filter tags by selected type
  const filteredTagsByType = !selectedTagType || selectedTagType === 'all' ?
  allUniqueTags :
  allUniqueTags.filter((tag) => tag.endsWith(`(${selectedTagType})`));

  // Helper to toggle filter selection
  const toggleFilter = (filterType, value) => {
    setFilters((prev) => {
      const current = prev[filterType];
      const newValues = current.includes(value) ?
      current.filter((v) => v !== value) :
      [...current, value];
      return { ...prev, [filterType]: newValues };
    });
  };

  const createTaskMutation = useMutation({
    mutationFn: async (data) => {
      const createdTask = await base44.entities.DailyTask.create(data);
      return { createdTask, originalData: data };
    },
    onSuccess: async ({ createdTask, originalData }) => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      
      // Send notification for tentative tasks assigned to other officers
      if (originalData.status === 'tentative' && originalData.user_email !== user?.email) {
        try {
          const activity = allActivities?.find(a => a.id === originalData.activity_id);
          await base44.functions.invoke('sendNotification', {
            recipient_email: originalData.user_email,
            type: 'task_tentative',
            source_type: 'daily_task',
            source_id: createdTask.id,
            source_code: activity?.activity_code,
            source_name: originalData.task_description,
            triggered_by_email: user?.email,
            deep_link_params: {
              date: originalData.date,
              user_email: originalData.user_email,
              task_id: createdTask.id
            },
            metadata: {
              task_description: originalData.task_description,
              activity_code: activity?.activity_code
            }
          });
        } catch (err) {
          console.error('Failed to send tentative task notification:', err);
        }
      }
      
      setAddTaskDialog(false);
      setTaskForm({
        activity_id: '',
        task_description: '',
        key_result_focus: '',
        time_allocation_percent: 40,
        task_order: 1,
        status_flag: 'gray',
        tags: [],
        assign_to_email: '',
        is_tentative: false,
        category: null,
        xp_value: 0,
        ep_value: 0,
        skill_rewards: [],
        bot_suggested_category: null,
        bot_suggested_xp: null,
        bot_suggested_ep: null,
        bot_suggested_skills: null,
        bot_suggestion_approved: false
      });
      setDialogMissionFilter([]);
      setDialogObjectiveFilter([]);
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.DailyTask.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.DailyTask.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  const getDateRange = () => {
    const ranges = [
    [0], // today only
    [-1, 0, 1], // yesterday, today, tomorrow
    [-3, -2, -1, 0, 1, 2, 3], // +/- 3 days
    [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7] // +/- 7 days
    ];
    return ranges[zoomLevel].map((offset) => addDays(centerDate, offset));
  };

  const dates = getDateRange();

  // Calculate grid template columns - center day gets 50%, others share remaining 50%
  const getGridTemplateColumns = () => {
    if (zoomLevel === 0) return '1fr';
    const totalDays = dates.length;
    const otherDays = totalDays - 1;
    const otherWidth = otherDays > 0 ? 50 / otherDays : 0;

    return dates.map((date, idx) => {
      const isCenter = isSameDay(date, centerDate);
      return isCenter ? '50%' : `${otherWidth}%`;
    }).join(' ');
  };

  const getTasksForDate = (date, userEmail = user?.email, includesPending = false) => {
    return dailyTasks.
    filter((task) => {
      if (task.user_email !== userEmail) return false;
      if (!includesPending && task.status === 'pending_approval') return false;
      // Tentative tasks: hide from main list ONLY when they're awaiting acceptance
      // from someone other than the current viewer. Self-owned tentatives (the user
      // is the assignee AND either the creator or no creator is recorded) belong in
      // the main list so the creator can actually see what they just saved.
      if (task.status === 'tentative') {
        const isSelfOwnedTentative = task.user_email === userEmail &&
          (task.created_by_email === userEmail || !task.created_by_email);
        if (!isSelfOwnedTentative) return false;
      }
      if (task.status === 'rejected') return false; // Don't show rejected in main list
      
      // Status filter (active/archived)
      if (statusFilterMode === 'active' && task.archived) return false;
      if (statusFilterMode === 'archived' && !task.archived) return false;
      
      // Conquered filter (status_flag)
      if (conqueredFilter === 'conquered' && task.status_flag !== 'green') return false;
      if (conqueredFilter === 'imminent' && task.status_flag !== 'yellow') return false;
      if (conqueredFilter === 'struggling' && task.status_flag !== 'red') return false;
      if (conqueredFilter === 'not_started' && task.status_flag !== 'gray') return false;
      
      const taskDate = new Date(task.date + 'T00:00:00');
      return isSameDay(taskDate, date);
    }).
    sort((a, b) => a.task_order - b.task_order);
  };

  const getTentativeTasksForDate = (date, userEmail) => {
    return dailyTasks.
    filter((task) => {
      if (task.user_email !== userEmail) return false;
      if (task.status !== 'tentative') return false;
      // Only show as tentative if created by someone else (not self-created)
      if (task.created_by_email === userEmail || !task.created_by_email) return false;
      const taskDate = new Date(task.date + 'T00:00:00');
      return isSameDay(taskDate, date);
    }).
    sort((a, b) => a.task_order - b.task_order);
  };

  const getRejectedTasksForDate = (date, userEmail, forSquadMember = null) => {
    return dailyTasks.
    filter((task) => {
      if (task.status !== 'rejected') return false;
      const taskDate = new Date(task.date + 'T00:00:00');
      if (!isSameDay(taskDate, date)) return false;
      
      // If viewing for a squad member, show rejected tasks that:
      // 1. Were created by current user AND assigned to that squad member
      if (forSquadMember) {
        return task.created_by_email === userEmail && task.user_email === forSquadMember;
      }
      
      // For current user's own section, don't show rejected tasks here
      // (they should appear under the squad member they were assigned to)
      return false;
    }).
    sort((a, b) => a.task_order - b.task_order);
  };

  const getPendingTasksForDate = (date, userEmail) => {
    return dailyTasks.
    filter((task) => {
      if (task.user_email !== userEmail) return false;
      if (task.status !== 'pending_approval') return false;
      const taskDate = new Date(task.date + 'T00:00:00');
      return isSameDay(taskDate, date);
    }).
    sort((a, b) => a.task_order - b.task_order);
  };

  const getNextTaskOrder = (date) => {
    const tasks = getTasksForDate(date);
    if (tasks.length === 0) return 1;
    if (tasks.length === 1) return 2;
    if (tasks.length === 2) return 3;
    return null; // Max 3 tasks
  };

  const handleAddTask = (date, forOfficerEmail = null, isTentative = false) => {
    const targetEmail = forOfficerEmail || user?.email;
    const tasks = dailyTasks.filter((task) => {
      if (task.user_email !== targetEmail) return false;
      const taskDate = new Date(task.date + 'T00:00:00');
      return isSameDay(taskDate, date);
    }).sort((a, b) => a.task_order - b.task_order);

    if (tasks.length >= 3 && tasks.length < 10 && !forOfficerEmail) {
      const proceed = window.confirm(
        'You are adding more than 3 tasks. The top 3 tasks should be your main focus areas. Additional tasks (4+) are for lower priority items.\n\nDo you want to proceed?'
      );
      if (!proceed) return;
    }
    
    if (tasks.length >= 10 && !forOfficerEmail) {
      alert('Maximum of 10 tasks per day reached. Focus on completing existing tasks before adding more.');
      return;
    }

    const nextOrder = tasks.length + 1;
    const defaultAllocation = nextOrder === 1 ? 40 : nextOrder === 2 ? 30 : 20;

    setSelectedDate(startOfDay(date));
    setTaskForm({
      ...taskForm,
      task_order: nextOrder,
      time_allocation_percent: defaultAllocation,
      assign_to_email: forOfficerEmail || '',
      is_tentative: isTentative
    });
    setAddTaskDialog(true);
  };

  const handleDateClick = (date) => {
    setCenterDate(date);
    if (zoomLevel > 1) {
      setZoomLevel(1); // Reset to 3-day view
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      activity_id: task.activity_id || '',
      task_description: task.task_description,
      key_result_focus: task.key_result_focus || '',
      time_allocation_percent: task.time_allocation_percent,
      task_order: task.task_order,
      status_flag: task.status_flag,
      tags: task.tags || [],
      assign_to_email: task.user_email !== user?.email ? task.user_email : '',
      is_tentative: task.status === 'tentative' || task.status === 'rejected'
    });
    // Set dialog filters based on the editing task's activity
    if (task.activity_id) {
      const activity = allActivities.find(a => a.id === task.activity_id);
      if (activity) {
        const objective = objectives.find(o => o.id === activity.objective_id);
        setDialogMissionFilter(objective?.mission_id ? [objective.mission_id] : []);
        setDialogObjectiveFilter(objective?.id ? [objective.id] : []);
      } else {
        setDialogMissionFilter([]);
        setDialogObjectiveFilter([]);
      }
    } else {
      setDialogMissionFilter([]);
      setDialogObjectiveFilter([]);
    }
    setEditTaskDialog(true);
  };

  const handleSubmitTask = () => {
    if (!taskForm.task_description) {
      alert('Task description is required');
      return;
    }

    if (!taskForm.activity_id) {
      alert('Activity is required');
      return;
    }

    // Determine target user email
    const targetUserEmail = taskForm.assign_to_email || user.email;
    const targetOfficer = officers.find(o => o.user_email === targetUserEmail);
    const isAIOfficer = targetOfficer?.custom_role === 'AI Officer';

    // Skip time allocation validation for AI officers
    if (!isAIOfficer) {
      // Validate time allocation based on task order
      const minMax = {
        1: { min: 40, max: 100 },
        2: { min: 30, max: 60 },
        3: { min: 0, max: 100 }
      };

      // Tasks 4+ have no strict time allocation requirements
      if (taskForm.task_order <= 3) {
        const taskOrderValidation = minMax[taskForm.task_order];
        if (!taskOrderValidation) {
          alert('Invalid task order. Please select a valid task order.');
          return;
        }
        const { min, max } = taskOrderValidation;
        if (taskForm.time_allocation_percent < min || taskForm.time_allocation_percent > max) {
          alert(`Task ${taskForm.task_order} must be between ${min}% and ${max}% of your time`);
          return;
        }
      }
    } else {
      // AI Officers don't need time allocation - set to 0
      taskForm.time_allocation_percent = 0;
    }

    if (editingTask) {
      updateTaskMutation.mutate({
        id: editingTask.id,
        data: {
          ...editingTask,
          ...taskForm,
          user_email: targetUserEmail,
          status: taskForm.is_tentative ? 'tentative' : (editingTask.status === 'rejected' ? 'tentative' : editingTask.status) // Resubmit if rejected
        }
      });
      setEditTaskDialog(false);
      setEditingTask(null);
    } else {
      // Determine status:
      // - If is_tentative was set by the form (e.g. user picked an activity they
      //   are not responsible/accountable for), honor that flag — save as tentative
      //   so the dialog's "Creating tentative task" promise matches what's stored.
      // - If creating for yourself (no assign_to_email or assign_to_email === user.email): 'approved'
      // - If assigning to an AI Officer (bot): 'approved' (bots don't need to accept tasks)
      // - If creating for someone else (human): 'tentative' (requires their acceptance)
      const isCreatingForSelf = !taskForm.assign_to_email || taskForm.assign_to_email === user.email;
      const isAssigningToBot = isAIOfficer; // isAIOfficer is already defined above based on targetOfficer
      const wantsTentative = !!taskForm.is_tentative;
      const taskStatus = wantsTentative
        ? 'tentative'
        : ((isCreatingForSelf || isAssigningToBot) ? 'approved' : 'tentative');
      
      const taskData = {
        ...taskForm,
        date: format(startOfDay(selectedDate), 'yyyy-MM-dd'),
        user_email: targetUserEmail, // Set after spread to ensure it's not overwritten
        status: taskStatus,
        generated_by_ai: false,
        // Ensure gamification fields are included
        category: taskForm.category,
        xp_value: taskForm.xp_value || 0,
        ep_value: taskForm.ep_value || 0,
        skill_rewards: taskForm.skill_rewards || [],
        bot_suggested_category: taskForm.bot_suggested_category,
        bot_suggested_xp: taskForm.bot_suggested_xp,
        bot_suggested_ep: taskForm.bot_suggested_ep,
        bot_suggested_skills: taskForm.bot_suggested_skills,
        bot_suggestion_approved: taskForm.bot_suggestion_approved || false
      };
      
      // Stamp created_by_email for any tentative task. Two cases:
      //   1. Cross-user assignment (existing behavior — required by getTentativeTasksForDate)
      //   2. Self-owned tentative (new — required by the updated getTasksForDate rule
      //      so the task is visible to its creator after save)
      if (taskStatus === 'tentative') {
        taskData.created_by_email = user.email;
      }
      
      delete taskData.assign_to_email;
      delete taskData.is_tentative;
      
      createTaskMutation.mutate(taskData);
      setAddTaskDialog(false);
    }

    setTaskForm({
      activity_id: '',
      task_description: '',
      key_result_focus: '',
      time_allocation_percent: 40,
      task_order: 1,
      status_flag: 'yellow',
      tags: [],
      assign_to_email: '',
      is_tentative: false,
      category: null,
      xp_value: 0,
      ep_value: 0,
      skill_rewards: [],
      bot_suggested_category: null,
      bot_suggested_xp: null,
      bot_suggested_ep: null,
      bot_suggested_skills: null,
      bot_suggestion_approved: false
    });
    setCurrentTagType('');
    setCurrentTagValue('');
  };

  const addTag = () => {
    if (currentTagType && currentTagValue.trim()) {
      const tagString = `${currentTagValue.trim()} (${currentTagType})`;
      if (!taskForm.tags.includes(tagString)) {
        setTaskForm({ ...taskForm, tags: [...taskForm.tags, tagString] });
        setCurrentTagType('');
        setCurrentTagValue('');
      }
    }
  };

  const removeTag = (tagToRemove) => {
    setTaskForm({ ...taskForm, tags: taskForm.tags.filter((tag) => tag !== tagToRemove) });
  };



  const handleArtifactUpload = async (e, task) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingArtifact(true);
    try {
      // 1. Upload to Google Drive
      const uploadResponse = await base44.functions.invoke('uploadToGoogleDrive', {
        file: file,
        context_type: 'Task',
        parent_context_id: task.id
      });

      if (!uploadResponse.data.success) {
        throw new Error(uploadResponse.data.error || 'Upload failed');
      }

      const doc = uploadResponse.data.document;

      // 2. Create Artifact
      await base44.entities.Artifact.create({
        name: doc.name,
        description: `Artifact created from task: ${task.task_description.substring(0, 50)}...`,
        category: 'other',
        type: 'uploaded',
        file_url: doc.drive_web_link,
        owner_email: user?.email,
        status: 'pending_approval',
        linked_task_id: task.id,
        tags: task.tags || []
      });

      alert('Artifact uploaded successfully and sent for approval!');
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    } catch (error) {
      console.error('Artifact upload error:', error);
      alert(`Failed to upload artifact: ${error.message}`);
    } finally {
      setUploadingArtifact(false);
    }
  };

  // Shared bot execution handler - used by both Plan dialog approval and re-run
  const executeBotTask = async (task, additionalContext) => {
    setExecutionDialog({ open: true, taskId: task.id, progress: 0, completed: false });
    try {
      const progressInterval = setInterval(() => {
        setExecutionDialog(prev => ({
          ...prev,
          progress: Math.min(prev.progress + Math.random() * 15, 90)
        }));
      }, 500);

      const result = await base44.functions.invoke('executeBotTask', {
        task_id: task.id,
        additional_context: additionalContext || undefined
      });
      clearInterval(progressInterval);
      setExecutionDialog(prev => ({ ...prev, progress: 100, completed: true }));
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });

      console.log('[executeBotTask] Response keys:', Object.keys(result.data));
      console.log('[executeBotTask] document_output present:', !!result.data.document_output, 'content length:', result.data.document_output?.content?.length || 0);

      // Determine document output - backend should provide it, but if not, synthesize from result text
      let docOutput = result.data.document_output;
      
      if (!docOutput || !docOutput.content) {
        // FRONTEND FALLBACK: Backend didn't produce document_output, synthesize from result text
        let resultText = result.data.result || '';
        
        // Sanitize [object Object] from result text
        resultText = resultText.replace(/\[object Object\]/g, '').replace(/\(see details below\)/g, '').trim();
        
        if (resultText.length > 50) {
          console.log('[executeBotTask] FRONTEND FALLBACK: Synthesizing document from result text, length:', resultText.length);
          const taskDesc = task.task_description || 'bot-deliverable';
          docOutput = {
            suggested_file_name: taskDesc.toLowerCase().replace(/[^a-z0-9]+/g, '-').substring(0, 50) + '.md',
            content: `# ${task.task_description}\n\n${resultText}`,
            task_id: task.id
          };
        }
      }
      
      // Sanitize any [object Object] remnants from document content
      if (docOutput && docOutput.content) {
        docOutput.content = docOutput.content.replace(/\[object Object\]/g, '');
      }

      if (docOutput && docOutput.content) {
        console.log('[executeBotTask] DOCUMENT READY - opening review dialog. Content length:', docOutput.content.length);
        const finalDocOutput = {
          ...docOutput,
          task_id: docOutput.task_id || task.id
        };
        setTimeout(() => {
          setExecutionDialog({ open: false, taskId: null, progress: 0, completed: false });
          setDocumentReviewDialog({ open: true, documentOutput: finalDocOutput, taskId: task.id, taskUserEmail: task.user_email });
        }, 1000);
      } else if (result.data.suggested_artifacts && result.data.suggested_artifacts.length > 0) {
        setTimeout(() => {
          setExecutionDialog({ open: false, taskId: null, progress: 0, completed: false });
          setArtifactApprovalDialog({ open: true, artifacts: result.data.suggested_artifacts, uploadedFiles: result.data.uploaded_files || [], taskUserEmail: task.user_email });
        }, 1000);
      } else {
        setTimeout(() => {
          setExecutionDialog({ open: false, taskId: null, progress: 0, completed: false });
        }, 1500);
      }
    } catch (error) {
      setExecutionDialog({ open: false, taskId: null, progress: 0, completed: false });
      alert(`Failed to execute task: ${error.response?.data?.error || error.message}`);
    }
  };

  const selectedActivity = allActivities.find((a) => a.id === taskForm.activity_id);

  return (
    <>
      <div className="mb-8">
        <div className="p-6 pb-4 border-b border-slate-300">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 md:gap-3">
          <CalendarIcon className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
          <div className="text-lg md:text-2xl font-bold text-slate-900">Daily Tasks</div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-end">
          {isCommander && !isMobile && (
          <Button
          size="sm"
          variant="outline"
          onClick={() => {
          const tasksWithPrinciples = dailyTasks.filter(t => 
            (t.principles_adhered?.length > 0 || t.red_line_considerations?.length > 0 || t.narrative_on_principles) &&
            !t.principles_validated
          );
          setValidatingTasks(tasksWithPrinciples);
          }}
          className="border-indigo-500/30 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20"
          disabled={dailyTasks.filter(t => 
          (t.principles_adhered?.length > 0 || t.red_line_considerations?.length > 0 || t.narrative_on_principles) &&
          !t.principles_validated
          ).length === 0}
          >
          <Shield className="w-4 h-4 mr-2" />
          Validate Principles ({dailyTasks.filter(t => 
          (t.principles_adhered?.length > 0 || t.red_line_considerations?.length > 0 || t.narrative_on_principles) &&
          !t.principles_validated
          ).length})
          </Button>
          )}
              {!isMobile && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setZoomLevel(Math.max(0, zoomLevel - 1))}
                    disabled={zoomLevel === 0}
                    className="border-slate-300 text-slate-900 hover:bg-slate-100">
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setZoomLevel(Math.min(3, zoomLevel + 1))}
                    disabled={zoomLevel === 3}
                    className="border-slate-300 text-slate-900 hover:bg-slate-100">
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCenterDate(subDays(centerDate, 1))}
                className="border-slate-300 text-slate-900 hover:bg-slate-100 h-8 w-8 p-0 md:h-auto md:w-auto md:px-3">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCenterDate(addDays(centerDate, 1))}
                className="border-slate-300 text-slate-900 hover:bg-slate-100 h-8 w-8 p-0 md:h-auto md:w-auto md:px-3">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-300 text-slate-900 hover:bg-slate-100 text-xs md:text-sm">
                    {format(centerDate, isMobile ? 'MMM d' : 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-slate-300" align="end">
                  <Calendar
                    mode="single"
                    selected={centerDate}
                    onSelect={(date) => date && setCenterDate(date)}
                    initialFocus />

                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Squad Selection - Hidden on mobile */}
          <div className={`border-t border-slate-300 pt-4 ${isMobile ? 'hidden' : ''}`}>
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-semibold text-slate-400">My Squad</h3>
              
              <Popover open={squadPopoverOpen} onOpenChange={setSquadPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="default" className="px-3 text-xs font-medium h-8" style={{ backgroundColor: '#16a34a', color: '#ffffff', borderColor: '#15803d' }}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Officer
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-800" align="start">
                  <Command className="bg-slate-100">
                    <CommandInput placeholder="Search officers..." className="border-none" />
                    <CommandList>
                      <CommandEmpty>No officers found.</CommandEmpty>
                      <CommandGroup className="bg-slate-100 text-foreground p-1 overflow-hidden [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                        {officers.
                        filter((o) => o.user_email !== user?.email && !squadEmails.includes(o.user_email)).
                        map((officer) =>
                        <CommandItem
                          key={officer.id}
                          value={`${officer.callsign} ${officer.rank_title} ${officer.user_email}`}
                          onSelect={() => addSquadMember(officer.user_email)}
                          className="cursor-pointer">

                              <div className="flex items-center gap-2">
                                <span className="text-sm">
                                  {officer.rank_title} {officer.callsign}
                                </span>
                                <span className="text-xs text-slate-500">
                                  Lvl {calculateLevel(officer.total_xp || 0)}
                                </span>
                              </div>
                            </CommandItem>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {squadEmails.length > 0 &&
            <div className="flex flex-wrap gap-2">
                {squadEmails.map((email) => {
                const officer = getOfficer(email);
                if (!officer) return null;
                const isBot = officer.is_bot || officer.custom_role === 'AI Officer';
                return (
                  <Badge
                    key={email}
                    variant="outline"
                    className={`pr-1 ${isBot ? 'bg-purple-900/50 border-purple-500/30 text-purple-300' : 'bg-slate-900/50 border-slate-700 text-slate-300'}`}>

                      {isBot && '🤖 '}{officer.rank_title} {officer.callsign}
                      <button
                      onClick={() => removeSquadMember(email)}
                      className="ml-2 hover:text-red-400 transition-colors">

                        <X className="w-3 h-3" />
                      </button>
                    </Badge>);

              })}
              </div>
            }
          </div>

          {/* Filters Section - Hidden on mobile */}
          <div className={`border-t border-slate-300 pt-4 mt-4 ${isMobile ? 'hidden' : ''}`}>
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-semibold text-slate-400">Filters</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Status</label>
                <Select value={conqueredFilter} onValueChange={setConqueredFilter}>
                  <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="conquered">Conquered</SelectItem>
                    <SelectItem value="imminent">Imminently Conquering</SelectItem>
                    <SelectItem value="struggling">Struggling</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">View</label>
                <Select value={statusFilterMode} onValueChange={setStatusFilterMode}>
                  <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Missions</label>
                <SearchableTagSelect
                multiple
                options={missions.map(m => ({
                  value: m.id,
                  label: `${m.mission_code ? `[${m.mission_code}] ` : ''}${m.title}`,
                  isMy: m.owner_email === user?.email || m.assigned_to_email === user?.email
                }))}
                value={filters.missions}
                onValueChange={(values) => setFilters(prev => ({ ...prev, missions: values }))}
                placeholder="All missions"
                emptyMessage="No missions found."
                className="bg-slate-50 border-slate-300 text-slate-900 h-auto min-h-[32px] text-xs w-full justify-start"
                renderItem={(option) => (
                  <span className="flex items-center">
                    {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                    {option.label}
                  </span>
                )}
              />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Objectives</label>
                <SearchableTagSelect
                multiple
                options={objectives.map(o => ({
                  value: o.id,
                  label: `${o.objective_code ? `[${o.objective_code}] ` : ''}${o.statement}`,
                  isMy: o.owner_email === user?.email
                }))}
                value={filters.objectives}
                onValueChange={(values) => setFilters(prev => ({ ...prev, objectives: values }))}
                placeholder="All objectives"
                emptyMessage="No objectives found."
                className="bg-slate-50 border-slate-300 text-slate-900 h-auto min-h-[32px] text-xs w-full justify-start"
                renderItem={(option) => (
                  <span className="flex items-center">
                    {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                    {option.label}
                  </span>
                )}
              />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Activities</label>
                <SearchableTagSelect
                multiple
                options={allActivities.map(a => {
                  const ro = officers.find(o => o.user_email === a.responsible_email);
                  return {
                    value: a.id,
                    label: `[${a.activity_code}] ${a.title}`,
                    isMy: a.accountable_email === user?.email || a.responsible_email === user?.email,
                    isAI: ro?.custom_role === 'AI Officer'
                  };
                })}
                value={filters.activities}
                onValueChange={(values) => setFilters(prev => ({ ...prev, activities: values }))}
                placeholder="All activities"
                emptyMessage="No activities found."
                className="bg-slate-50 border-slate-300 text-slate-900 h-auto min-h-[32px] text-xs w-full justify-start"
                renderItem={(option) => (
                  <span className="flex items-center">
                    {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                    {option.isAI && <span className="text-purple-400 mr-1">🤖</span>}
                    {option.label}
                  </span>
                )}
              />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Tag Type</label>
                <Select
                  value={selectedTagType}
                  onValueChange={setSelectedTagType}>

                  <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900 h-8 text-xs">
                    <SelectValue placeholder="Select tag type" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueTagTypes.map((type, idx) =>
                    <SelectItem key={idx} value={type}>
                        {type}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Tag Value</label>
                <Select
                  value=""
                  onValueChange={(tag) => {
                    if (!filters.tags.includes(tag)) {
                      setFilters({ ...filters, tags: [...filters.tags, tag] });
                    }
                  }}
                  disabled={!selectedTagType}>

                  <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900 h-8 text-xs">
                    <SelectValue placeholder={selectedTagType ? 'Select tag...' : 'Select type first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTagsByType.map((tag, idx) =>
                    <SelectItem key={idx} value={tag}>
                        {tag}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs text-slate-500 mb-1 block">Selected Tags</label>
                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 border border-slate-300 rounded-lg min-h-[32px]">
                  {filters.tags.length === 0 ?
                  <span className="text-xs text-slate-500">No tags selected</span> :

                  filters.tags.map((tag, idx) => {
                    const match = tag.match(/\(([^)]+)\)$/);
                    const tagType = match ? match[1] : '';
                    return (
                      <Badge
                        key={idx}
                        variant="outline"
                        className={`text-xs ${
                        tagType === 'Officer' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30' :
                        tagType === 'Artifact' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' :
                        tagType === 'Ritual' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' :
                        tagType === 'Policy' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                        'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'}`
                        }>

                          {tag}
                          <button
                          onClick={() => setFilters({ ...filters, tags: filters.tags.filter((t) => t !== tag) })}
                          className="ml-1 hover:text-red-400">

                            <X className="w-3 h-3" />
                          </button>
                        </Badge>);

                  })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-6">
          <div
            className="grid gap-6"
            style={{ gridTemplateColumns: getGridTemplateColumns() }}>

            {dates.map((date, idx) => {
              const isToday = isSameDay(date, new Date());
              const tasks = getTasksForDate(date);
              const totalAllocation = tasks.reduce((sum, task) => sum + (task.time_allocation_percent || 0), 0);
              const exceeds100 = totalAllocation > 100;

              return (
                <div key={idx} className="relative">
                  {/* Day separator line */}
                  {idx > 0 &&
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-800" />
                  }

                  <div className="pl-0 md:pl-4 md:first:pl-0">
                    {/* Day header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDateClick(date)}
                          className={`text-sm ${isToday ? 'font-bold text-purple-400' : 'text-slate-400'} hover:text-white transition-colors`}>

                          {format(date, 'EEE MMM d')}
                        </button>
                        {exceeds100 &&
                        <span className="text-xs font-bold text-red-600">Exceeds 100%</span>
                        }
                      </div>
                      {zoomLevel <= 1 &&
                      <button
                        onClick={() => handleAddTask(date)}
                        className="h-7 w-7 rounded-full bg-indigo-500 hover:bg-indigo-400 flex items-center justify-center transition-colors"
                        title="Add task">
                        <Plus className="w-4 h-4 text-white" />
                      </button>
                      }
                    </div>

                    {/* Task cards - show details only on zoom levels 0 and 1 */}
                    {zoomLevel <= 1 ?
                    <div className="space-y-6">
                        {/* Current User's Tasks */}
                        <div>
                          <div className="flex items-center gap-2 mb-3 w-full">
                          <button
                          onClick={() => toggleOfficerCollapse(user?.email)}
                          className="flex items-center gap-2 hover:text-white transition-colors flex-1"
                          title={`${getOfficer(user?.email)?.rank_title} ${getOfficer(user?.email)?.callsign} • Lvl ${calculateLevel(getOfficer(user?.email)?.total_xp || 0)}`}>

                            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate flex-1 text-left">
                              {(getOfficer(user?.email)?.is_bot || getOfficer(user?.email)?.custom_role === 'AI Officer') && '🤖 '}
                              {getOfficer(user?.email)?.rank_title} {getOfficer(user?.email)?.callsign} • Lvl {calculateLevel(getOfficer(user?.email)?.total_xp || 0)}
                            </div>
                            {collapsedOfficers.includes(user?.email) ?
                          <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" /> :

                          <ChevronUp className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          }
                          </button>
                          <QuickTodoButton 
                            officer={getOfficer(user?.email)} 
                            onClick={() => setMicroTaskListDialog({ open: true, officer: getOfficer(user?.email) })}
                          />
                          </div>
                          {collapsedOfficers.includes(user?.email) ?
                        <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">
                              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                            </Badge> :

                        <div className="space-y-3">
                            {/* Show tentative tasks for current user */}
                            {(() => {
                              const tentativeTasks = getTentativeTasksForDate(date, user?.email);
                              if (tentativeTasks.length === 0) return null;
                              
                              return (
                                <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-300">
                                      <AlertTriangle className="w-4 h-4" />
                                      Tentative Tasks ({tentativeTasks.length})
                                    </div>
                                    {(() => {
                                      const tasksNeedingMyAcceptance = tentativeTasks.filter(t => {
                                        // For current user's own tasks - they can accept if assigned to them
                                        return t.user_email === user?.email;
                                      });
                                      
                                      if (tasksNeedingMyAcceptance.length === 0) return null;
                                      
                                      return (
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={async () => {
                                              await Promise.all(tasksNeedingMyAcceptance.map(t => 
                                                base44.entities.DailyTask.update(t.id, { ...t, status: 'approved' })
                                              ));
                                              queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                            }}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                                            Accept All
                                          </Button>
                                          <Button
                                            size="sm"
                                            onClick={() => {
                                              // For bulk rejection, open dialog with first task (they'll all get same reason)
                                              setRejectionDialog({ open: true, task: tasksNeedingMyAcceptance, reason: '', isBulk: true });
                                            }}
                                            variant="outline"
                                            className="border-red-500/30 text-red-400 hover:bg-red-500/20 h-7 text-xs">
                                            Reject All
                                          </Button>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div className="space-y-2">
                                    {tentativeTasks.map(task => {
                                     const activity = allActivities.find(a => a.id === task.activity_id);
                                     // For current user's tasks, they can accept/reject if assigned to them
                                     const canAccept = task.user_email === user?.email;
                                     const isCreator = task.created_by_email === user?.email;
                                      
                                      return (
                                        <div 
                                          key={task.id} 
                                          onClick={() => setTaskDetailDialog({ open: true, task })}
                                          className="p-2 bg-white rounded border border-amber-500/20 cursor-pointer hover:bg-slate-50 transition-colors">
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1">
                                              <p className="text-xs text-slate-900 mb-1">{task.task_description}</p>
                                              <div className="text-[10px] text-slate-600">
                                                {canAccept ? `Created by ${officers.find(o => o.user_email === task.created_by_email)?.callsign || task.created_by_email} - awaiting your acceptance` : `Awaiting acceptance from ${officers.find(o => o.user_email === task.user_email)?.callsign || task.user_email}`}
                                              </div>
                                            </div>
                                            <div className="flex gap-1">
                                              {canAccept ? (
                                                <>
                                                  <Button
                                                    size="icon"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      await base44.entities.DailyTask.update(task.id, { ...task, status: 'approved' });
                                                      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                                    }}
                                                    className="h-6 w-6 bg-emerald-600 hover:bg-emerald-700">
                                                    <Check className="w-3 h-3" />
                                                  </Button>
                                                  <Button
                                                    size="icon"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setRejectionDialog({ open: true, task, reason: '' });
                                                    }}
                                                    variant="outline"
                                                    className="h-6 w-6 border-red-500/30 text-red-400 hover:bg-red-500/20">
                                                    <X className="w-3 h-3" />
                                                  </Button>
                                                  </>
                                                  ) : isCreator ? (
                                                <>
                                                  <Button
                                                    size="icon"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleEditTask(task);
                                                    }}
                                                    className="h-6 w-6 bg-indigo-600 hover:bg-indigo-700">
                                                    <Edit2 className="w-3 h-3" />
                                                  </Button>
                                                  <Button
                                                    size="icon"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      await base44.entities.DailyTask.delete(task.id);
                                                      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                                    }}
                                                    variant="outline"
                                                    className="h-6 w-6 border-red-500/30 text-red-400 hover:bg-red-500/20">
                                                    <Trash2 className="w-3 h-3" />
                                                  </Button>
                                                </>
                                              ) : null}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                );
                                })()}

                                {/* Rejected tasks for current user's own section are not shown here - they appear under the squad member */}
                                {(() => {
                                const rejectedTasks = []; // No rejected tasks in own section
                                if (rejectedTasks.length === 0) return null;

                                return (
                                <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <div className="flex items-center gap-2 text-sm font-semibold text-red-300 mb-2">
                                <X className="w-4 h-4" />
                                Rejected Tasks ({rejectedTasks.length})
                                </div>
                                <div className="space-y-2">
                                {rejectedTasks.map(task => (
                                  <div key={task.id} className="p-2 bg-white rounded border border-red-500/20">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1">
                                        <p className="text-xs text-slate-900 mb-1">{task.task_description}</p>
                                        <div className="text-[10px] text-slate-600">
                                          Rejected by {(() => {
                                            const activity = allActivities.find(a => a.id === task.activity_id);
                                            const approverEmail = activity?.accountable_email || activity?.responsible_email;
                                            const approver = officers.find(o => o.user_email === approverEmail);
                                            return approver?.callsign || approverEmail || 'activity owner';
                                          })()}
                                        </div>
                                      </div>
                                      <div className="flex gap-1">
                                        <Button
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditTask(task);
                                          }}
                                          className="h-6 w-6 bg-amber-600 hover:bg-amber-700"
                                          title="Edit and resubmit">
                                          <Edit2 className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            await base44.entities.DailyTask.delete(task.id);
                                            queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                          }}
                                          variant="outline"
                                          className="h-6 w-6 border-red-500/30 text-red-400 hover:bg-red-500/20"
                                          title="Delete permanently">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                </div>
                                </div>
                                );
                                })()}
                                {tasks.filter(taskMatchesFilters).map((task) =>
                          <div key={task.id} className={`relative transition-all duration-500 ${highlightedTaskId === task.id ? 'z-20 scale-105' : ''}`}>
                        {/* Flag buttons - positioned outside card on right edge */}
                        <div className="absolute right-0 top-0 z-10 translate-x-1/2">
                          <StatusFlagButtons
                            currentFlag={task.status_flag}
                            onChange={(flag) => handleStatusFlagChange(task.id, flag)}
                            includeGray={true}
                            size="sm"
                            variant="compact"
                          />
                        </div>

                        <Card 
                          onClick={() => setTaskDetailDialog({ open: true, task })}
                          className={`glass-panel border-slate-700 group hover:border-slate-600 transition-colors overflow-hidden cursor-pointer ${highlightedTaskId === task.id ? 'ring-2 ring-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.5)]' : ''}`}>
                          {/* Flag color bar at top 15% */}
                          <div className={`h-2 ${
                              task.status_flag === 'green' ? 'bg-emerald-500' :
                              task.status_flag === 'yellow' ? 'bg-amber-500' :
                              task.status_flag === 'red' ? 'bg-red-500' :
                              task.status_flag === 'gray' ? 'bg-slate-500' : 'bg-slate-700'}`
                              } />
                          
                          <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-indigo-600 text-white font-bold text-xs h-5 w-5 flex items-center justify-center p-0">
                                {task.task_order}
                              </Badge>
                              <span className="text-xs text-slate-500 font-semibold">{task.time_allocation_percent}%</span>
                              {task.generated_by_ai && <Bot className="w-3 h-3 text-indigo-400" title="AI Generated" />}
                              {(() => {
                                const activity = getActivity(task.activity_id);
                                if (activity && activity.responsible_email !== user?.email && activity.accountable_email !== user?.email) {
                                  return <span className="text-lg" title="Reinforcement - not your primary responsibility">💪</span>;
                                }
                                return null;
                              })()}
                            </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditTask(task);
                                      }}
                                      className="h-6 w-6 text-slate-400 hover:text-indigo-400">
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setArchiveDialog({ open: true, task });
                                  }}
                                  className="h-6 w-6 text-slate-400 hover:text-amber-400">
                                  <Archive className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (deleteConfirm === task.id) {
                                      deleteTaskMutation.mutate(task.id);
                                      setDeleteConfirm(null);
                                    } else {
                                      setDeleteConfirm(task.id);
                                      setTimeout(() => setDeleteConfirm(null), 3000);
                                    }
                                  }}
                                  className={`h-6 w-6 transition-all ${
                                    deleteConfirm === task.id 
                                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                                      : 'text-slate-400 hover:text-red-400'
                                  }`}>
                                  {deleteConfirm === task.id ? <X className="w-4 h-4" /> : <Trash2 className="w-3 h-3" />}
                                </Button>
                              </div>
                            </div>
                            {(() => {
                              const activity = getActivity(task.activity_id);
                              if (!activity) return null;
                              
                              // Check if activity is archived
                              if (activity.status === 'archived') {
                                return (
                                  <div className="mb-2">
                                    <ArchivedParentBadge 
                                      parentType="Activity"
                                      parentName={activity.title}
                                      parentPage="activities"
                                    />
                                  </div>
                                );
                              }
                              
                              // Check if parent objective is archived
                              const objective = objectives.find(o => o.id === activity.objective_id);
                              if (objective && objective.status === 'archived') {
                                return (
                                  <div className="mb-2">
                                    <ArchivedParentBadge 
                                      parentType="Objective"
                                      parentName={objective.statement}
                                      parentPage="discovery"
                                    />
                                  </div>
                                );
                              }
                              
                              // Check if grandparent mission is archived
                              if (objective) {
                                const mission = missions.find(m => m.id === objective.mission_id);
                                if (mission && mission.status === 'archived') {
                                  return (
                                    <div className="mb-2">
                                      <ArchivedParentBadge 
                                        parentType="Mission"
                                        parentName={mission.title}
                                        parentPage="missions"
                                      />
                                    </div>
                                  );
                                }
                              }
                              
                              return null;
                            })()}
                            <p className="text-slate-600 mb-2 text-sm line-clamp-2">{task.task_description}</p>
                            {task.status === 'tentative' && (
                              <Badge className="mb-2 text-xs bg-amber-500/20 text-amber-700 border-amber-500/40">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Tentative — awaiting approval
                              </Badge>
                            )}
                            {task.ai_assessment && task.ai_assessment.type !== 'ok' && (
                              <div className={`text-[10px] p-1.5 rounded-md flex items-start gap-1 mb-2 ${
                                task.ai_assessment.type === 'redundant' 
                                  ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                                  : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
                              }`}>
                                {task.ai_assessment.type === 'redundant' ? (
                                  <AlertOctagon className="w-3 h-3 mt-0.5 shrink-0" />
                                ) : (
                                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                                )}
                                <span className="leading-tight">{task.ai_assessment.message}</span>
                              </div>
                            )}
                            {task.principles_validated && (
                            <Badge className="mb-2 text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                            <Check className="w-3 h-3 mr-1" />
                            Principles Validated
                            </Badge>
                            )}
                            {task.tags && task.tags.length > 0 &&
                            <div className="flex gap-1 mb-2 overflow-x-auto">
                                {task.tags.map((tag, idx) => {
                                    const match = tag.match(/\(([^)]+)\)$/);
                                    const tagType = match ? match[1] : '';
                                    return (
                                      <Badge
                                        key={idx}
                                        variant="outline"
                                        title={tag}
                                        className={`text-[10px] py-0 px-1.5 h-4 whitespace-nowrap max-w-[80px] truncate ${
                                        tagType === 'Officer' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' :
                                        tagType === 'Artifact' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' :
                                        tagType === 'Ritual' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                                        tagType === 'Policy' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                        'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}`
                                        }>

                                      {tag}
                                    </Badge>);

                                  })}
                              </div>
                                }
                            {task.key_result_focus &&
                                <div className="flex items-start gap-1 text-xs text-slate-500 mb-1">
                                <Target className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-1">{task.key_result_focus}</span>
                              </div>
                                }
                            {task.activity_id && getActivity(task.activity_id) &&
                                <div className="text-xs text-slate-500 font-mono">
                                [{getActivity(task.activity_id).activity_code}] {getActivity(task.activity_id).title}
                              </div>
                                }
                          </CardContent>
                        </Card>
                      </div>
                          )}
                            {tasks.filter((t) => !taskMatchesFilters(t)).length > 0 &&
                          <Badge variant="outline" className="border-slate-600/30 text-slate-500 mt-2">
                                {tasks.filter((t) => !taskMatchesFilters(t)).length} other {tasks.filter((t) => !taskMatchesFilters(t)).length === 1 ? 'task' : 'tasks'}
                              </Badge>
                          }
                          </div>
                        }
                        </div>

                        {/* Squad Members' Tasks */}
                        {squadEmails.map((squadEmail) => {
                        const squadTasks = getTasksForDate(date, squadEmail);
                        const squadOfficer = getOfficer(squadEmail);
                        if (!squadOfficer) return null;
                        const isSquadBot = squadOfficer.is_bot || squadOfficer.custom_role === 'AI Officer';

                        return (
                          <div key={squadEmail}>
                              <div className="border-t-2 border-slate-700 pt-3 mb-3">
                                <div className="flex items-center gap-2 mb-3">
                                  <button
                                  onClick={() => toggleOfficerCollapse(squadEmail)}
                                  className="flex items-center gap-2 hover:text-white transition-colors flex-1"
                                  title={`${squadOfficer.rank_title} ${squadOfficer.callsign} • Lvl ${calculateLevel(squadOfficer.total_xp || 0)}`}>

                                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate flex-1 text-left">
                                      {isSquadBot && '🤖 '}{squadOfficer.rank_title} {squadOfficer.callsign} • Lvl {calculateLevel(squadOfficer.total_xp || 0)}
                                    </div>
                                    {collapsedOfficers.includes(squadEmail) ?
                                  <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" /> :

                                  <ChevronUp className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  }
                                  </button>
                                  <QuickTodoButton 
                                    officer={squadOfficer} 
                                    onClick={() => setMicroTaskListDialog({ open: true, officer: squadOfficer })}
                                  />
                                  {zoomLevel <= 1 && !collapsedOfficers.includes(squadEmail) && (() => {
                                    if (isSquadBot) {
                                      // Check if current user is the bot owner
                                      const isBotOwner = squadOfficer.bot_owner_email === user?.email;
                                      if (!isBotOwner) return null;

                                      return (
                                        <button
                                          onClick={() => handleAddTask(date, squadEmail)}
                                          className="h-7 w-7 rounded-full bg-purple-500 hover:bg-purple-400 flex items-center justify-center transition-colors"
                                          title="Create task for AI Officer">
                                          <Plus className="w-4 h-4 text-white" />
                                        </button>
                                      );
                                    } else {
                                      return (
                                        <button
                                          onClick={() => handleAddTask(date, squadEmail, true)}
                                          className="h-7 w-7 rounded-full bg-amber-500 hover:bg-amber-400 flex items-center justify-center transition-colors"
                                          title="Add tentative task (requires acceptance)">
                                          <Plus className="w-4 h-4 text-white" />
                                        </button>
                                      );
                                    }
                                  })()}
                                </div>
                              </div>
                              {collapsedOfficers.includes(squadEmail) ?
                            <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">
                                  {squadTasks.length} {squadTasks.length === 1 ? 'task' : 'tasks'}
                                </Badge> :

                            <div className="space-y-3">
                                {/* Show pending bot tasks approval section */}
                                {isSquadBot && (() => {
                                  const pendingTasks = getPendingTasksForDate(date, squadEmail);
                                  if (pendingTasks.length === 0) return null;

                                  return (
                                    <PendingBotTasksApproval
                                      tasks={pendingTasks}
                                      onApproveAll={async () => {
                                        await Promise.all(pendingTasks.map(t => 
                                          base44.entities.DailyTask.update(t.id, { ...t, status: 'approved' })
                                        ));
                                        queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                      }}
                                      onRejectAll={async () => {
                                        await Promise.all(pendingTasks.map(t => 
                                          base44.entities.DailyTask.delete(t.id)
                                        ));
                                        queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                      }}
                                    />
                                  );
                                })()}

                                {/* Show tentative tasks for this officer */}
                                {(() => {
                                  const tentativeTasks = getTentativeTasksForDate(date, squadEmail);
                                  if (tentativeTasks.length === 0) return null;

                                  return (
                                    <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-300">
                                          <AlertTriangle className="w-4 h-4" />
                                          Tentative Tasks ({tentativeTasks.length})
                                        </div>
                                        {(() => {
                                          // For squad member's tasks - they can accept/reject if assigned to them
                                          const tasksNeedingMyAcceptance = tentativeTasks.filter(t => t.user_email === squadEmail);
                                          
                                          if (tasksNeedingMyAcceptance.length === 0 || squadEmail !== user?.email) return null;
                                          
                                          return (
                                            <div className="flex gap-2">
                                              <Button
                                                size="sm"
                                                onClick={async () => {
                                                  await Promise.all(tasksNeedingMyAcceptance.map(t => 
                                                    base44.entities.DailyTask.update(t.id, { ...t, status: 'approved' })
                                                  ));
                                                  queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                                }}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                                                Accept All
                                              </Button>
                                              <Button
                                                size="sm"
                                                onClick={() => {
                                                  setRejectionDialog({ open: true, task: tasksNeedingMyAcceptance, reason: '', isBulk: true });
                                                }}
                                                variant="outline"
                                                className="border-red-500/30 text-red-400 hover:bg-red-500/20 h-7 text-xs">
                                                Reject All
                                              </Button>
                                              </div>
                                              );
                                              })()}
                                              </div>
                                              <div className="space-y-2">
                                              {tentativeTasks.map(task => {
                                              const activity = allActivities.find(a => a.id === task.activity_id);
                                              // For squad member view: if I'm viewing my own tasks in squad, I can accept/reject
                                              const canAccept = task.user_email === user?.email;
                                         const isCreator = task.created_by_email === user?.email;
                                          
                                          return (
                                            <div 
                                              key={task.id} 
                                              onClick={() => setTaskDetailDialog({ open: true, task })}
                                              className="p-2 bg-white rounded border border-amber-500/20 cursor-pointer hover:bg-slate-50 transition-colors">
                                              <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                  <p className="text-xs text-slate-900 mb-1">{task.task_description}</p>
                                                  <div className="text-[10px] text-slate-600">
                                                    {canAccept ? `Created by ${officers.find(o => o.user_email === task.created_by_email)?.callsign || task.created_by_email} - awaiting your acceptance` : `Awaiting acceptance from ${officers.find(o => o.user_email === squadEmail)?.callsign || squadEmail}`}
                                                  </div>
                                                </div>
                                                <div className="flex gap-1">
                                                  {canAccept ? (
                                                    <>
                                                      <Button
                                                        size="icon"
                                                        onClick={async (e) => {
                                                          e.stopPropagation();
                                                          await base44.entities.DailyTask.update(task.id, { ...task, status: 'approved' });
                                                          queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                                        }}
                                                        className="h-6 w-6 bg-emerald-600 hover:bg-emerald-700">
                                                        <Check className="w-3 h-3" />
                                                      </Button>
                                                      <Button
                                                        size="icon"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setRejectionDialog({ open: true, task, reason: '' });
                                                        }}
                                                        variant="outline"
                                                        className="h-6 w-6 border-red-500/30 text-red-400 hover:bg-red-500/20">
                                                        <X className="w-3 h-3" />
                                                      </Button>
                                                    </>
                                                  ) : isCreator ? (
                                                    <>
                                                      <Button
                                                        size="icon"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEditTask(task);
                                                        }}
                                                        className="h-6 w-6 bg-indigo-600 hover:bg-indigo-700">
                                                        <Edit2 className="w-3 h-3" />
                                                      </Button>
                                                      <Button
                                                        size="icon"
                                                        onClick={async (e) => {
                                                          e.stopPropagation();
                                                          await base44.entities.DailyTask.delete(task.id);
                                                          queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                                        }}
                                                        variant="outline"
                                                        className="h-6 w-6 border-red-500/30 text-red-400 hover:bg-red-500/20">
                                                        <Trash2 className="w-3 h-3" />
                                                      </Button>
                                                    </>
                                                  ) : null}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Show rejected tasks for squad officers (only visible to creator) */}
                                {(() => {
                                  const rejectedTasks = getRejectedTasksForDate(date, user?.email, squadEmail);
                                  if (rejectedTasks.length === 0) return null;
                                  
                                  return (
                                    <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                      <div className="flex items-center gap-2 text-sm font-semibold text-red-300 mb-2">
                                        <X className="w-4 h-4" />
                                        Rejected Tasks ({rejectedTasks.length})
                                      </div>
                                      <div className="space-y-2">
                                        {rejectedTasks.map(task => (
                                          <div key={task.id} className="p-2 bg-white rounded border border-red-500/20">
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <p className="text-xs text-slate-900 mb-1">{task.task_description}</p>
                                                <div className="text-[10px] text-slate-600">
                                                  Rejected by {(() => {
                                                    // The person who rejected is the one the task was assigned to (user_email)
                                                    const rejecter = officers.find(o => o.user_email === task.user_email);
                                                    return rejecter?.callsign || task.user_email || 'assignee';
                                                  })()}
                                                </div>
                                                {task.rejection_reason && (
                                                  <div className="text-[10px] text-red-400 mt-1 italic">
                                                    Reason: {task.rejection_reason}
                                                  </div>
                                                )}
                                              </div>
                                              <div className="flex gap-1">
                                                <Button
                                                  size="icon"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditTask(task);
                                                  }}
                                                  className="h-6 w-6 bg-amber-600 hover:bg-amber-700"
                                                  title="Edit and resubmit">
                                                  <Edit2 className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  size="icon"
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await base44.entities.DailyTask.delete(task.id);
                                                    queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                                                  }}
                                                  variant="outline"
                                                  className="h-6 w-6 border-red-500/30 text-red-400 hover:bg-red-500/20"
                                                  title="Delete permanently">
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                                {squadTasks.filter(taskMatchesFilters).map((task) =>
                              <Card 
                              onClick={() => setTaskDetailDialog({ open: true, task })}
                              key={task.id} className={`glass-panel border-slate-700 opacity-75 transition-all duration-500 cursor-pointer hover:opacity-100 ${highlightedTaskId === task.id ? 'ring-2 ring-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.5)] scale-105 opacity-100 z-10' : ''}`}>
                              <div className={`h-2 ${
                              task.status_flag === 'green' ? 'bg-emerald-500' :
                              task.status_flag === 'yellow' ? 'bg-amber-500' :
                              task.status_flag === 'red' ? 'bg-red-500' :
                              task.status_flag === 'gray' ? 'bg-slate-500' : 'bg-slate-700'}`
                              } />
                              <CardContent className="p-3">
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2">
                                          <Badge className="bg-indigo-600 text-white font-bold text-xs h-5 w-5 flex items-center justify-center p-0">
                                            {task.task_order}
                                          </Badge>
                                          <span className="text-xs text-slate-500 font-semibold">{task.time_allocation_percent}%</span>
                                          {task.generated_by_ai && <Bot className="w-3 h-3 text-indigo-400" title="AI Generated" />}
                                          {task.bot_execution_status === 'in_progress' && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Executing...</Badge>}
                                          {task.bot_execution_status === 'completed' && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">AI Done</Badge>}
                                          {task.bot_execution_status === 'failed' && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Failed</Badge>}
                                          {(() => {
                                            const activity = myActivities.find(a => a.id === task.activity_id);
                                            if (activity && activity.responsible_email !== squadEmail && activity.accountable_email !== squadEmail) {
                                              return <span className="text-lg" title="Reinforcement - not their primary responsibility">💪</span>;
                                            }
                                            return null;
                                          })()}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          {isSquadBot && task.bot_execution_status !== 'completed' && (
                                            <>
                                              <Popover open={modelSelectorOpen[task.id]} onOpenChange={(open) => setModelSelectorOpen({ ...modelSelectorOpen, [task.id]: open })}>
                                                <PopoverTrigger asChild>
                                                  <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-6 w-6 text-purple-400 hover:text-purple-300"
                                                    title={`Model: ${task.bot_execution_model || 'claude-haiku-4-5-20251001'}`}>
                                                    <Cpu className="w-3 h-3" />
                                                  </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[280px] p-2 bg-slate-900 border-slate-800" align="start" onClick={(e) => e.stopPropagation()}>
                                                  <div className="space-y-1">
                                                    {availableModels.map(model => (
                                                      <button
                                                        key={model.value}
                                                        onClick={async (e) => {
                                                          e.stopPropagation();
                                                          await updateTaskMutation.mutateAsync({
                                                            id: task.id,
                                                            data: { ...task, bot_execution_model: model.value }
                                                          });
                                                          setModelSelectorOpen({ ...modelSelectorOpen, [task.id]: false });
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-slate-800 transition-colors ${
                                                          (task.bot_execution_model || 'claude-haiku-4-5-20251001') === model.value 
                                                            ? 'bg-indigo-500/20 text-indigo-300' 
                                                            : 'text-slate-300'
                                                        }`}>
                                                        {model.label}
                                                      </button>
                                                    ))}
                                                  </div>
                                                </PopoverContent>
                                              </Popover>
                                              <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (task.completed) {
                                                    alert('Task already completed');
                                                    return;
                                                  }
                                                  setBotPlanDialog({ open: true, task });
                                                }}
                                                className="h-6 w-6 text-emerald-400 hover:text-emerald-300"
                                                title="View plan & execute"
                                                disabled={task.bot_execution_status === 'in_progress'}>
                                                <Play className="w-3 h-3" />
                                              </Button>
                                            </>
                                          )}
                                          {(() => {
                                            const activity = myActivities.find(a => a.id === task.activity_id);
                                            const canDelete = isCommander || (activity && activity.accountable_email === user?.email);
                                            return canDelete ? (
                                              <>
                                                {isSquadBot && task.bot_execution_status === 'completed' && (
                                                  <>
                                                    <Popover open={modelSelectorOpen[task.id]} onOpenChange={(open) => setModelSelectorOpen({ ...modelSelectorOpen, [task.id]: open })}>
                                                      <PopoverTrigger asChild>
                                                        <Button
                                                          size="icon"
                                                          variant="ghost"
                                                          onClick={(e) => e.stopPropagation()}
                                                          className="h-6 w-6 text-purple-400 hover:text-purple-300"
                                                          title={`Model: ${task.bot_execution_model || 'claude-haiku-4-5-20251001'}`}>
                                                          <Cpu className="w-3 h-3" />
                                                        </Button>
                                                      </PopoverTrigger>
                                                      <PopoverContent className="w-[280px] p-2 bg-slate-900 border-slate-800" align="start" onClick={(e) => e.stopPropagation()}>
                                                        <div className="space-y-1">
                                                          {availableModels.map(model => (
                                                            <button
                                                              key={model.value}
                                                              onClick={async (e) => {
                                                                e.stopPropagation();
                                                                await updateTaskMutation.mutateAsync({
                                                                  id: task.id,
                                                                  data: { ...task, bot_execution_model: model.value }
                                                                });
                                                                setModelSelectorOpen({ ...modelSelectorOpen, [task.id]: false });
                                                              }}
                                                              className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-slate-800 transition-colors ${
                                                                (task.bot_execution_model || 'claude-haiku-4-5-20251001') === model.value 
                                                                  ? 'bg-indigo-500/20 text-indigo-300' 
                                                                  : 'text-slate-300'
                                                              }`}>
                                                              {model.label}
                                                            </button>
                                                          ))}
                                                        </div>
                                                      </PopoverContent>
                                                    </Popover>
                                                    <Button
                                                      size="icon"
                                                      variant="ghost"
                                                      onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (task.completed) {
                                                          await updateTaskMutation.mutateAsync({
                                                            id: task.id,
                                                            data: { ...task, bot_execution_status: 'not_started', bot_execution_result: '', completed: false, completed_at: null }
                                                          });
                                                        }
                                                        setBotPlanDialog({ open: true, task });
                                                      }}
                                                      className="h-6 w-6 text-amber-400 hover:text-amber-300"
                                                      title="Re-run task with AI Officer">
                                                      <RotateCw className="w-3 h-3" />
                                                    </Button>
                                                  </>
                                                )}
                                                <Button
                                                  size="icon"
                                                  variant="ghost"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditTask(task);
                                                  }}
                                                  className="h-6 w-6 text-slate-400 hover:text-indigo-400">
                                                  <Edit2 className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  size="icon"
                                                  variant="ghost"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (deleteConfirm === task.id) {
                                                      deleteTaskMutation.mutate(task.id);
                                                      setDeleteConfirm(null);
                                                    } else {
                                                      setDeleteConfirm(task.id);
                                                      setTimeout(() => setDeleteConfirm(null), 3000);
                                                    }
                                                  }}
                                                  className={`h-6 w-6 transition-all ${
                                                    deleteConfirm === task.id 
                                                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                      : 'text-slate-400 hover:text-red-400'
                                                  }`}>
                                                  {deleteConfirm === task.id ? <X className="w-4 h-4" /> : <Trash2 className="w-3 h-3" />}
                                                </Button>
                                              </>
                                            ) : null;
                                          })()}
                                        </div>
                                      </div>
                                      <p className="text-slate-600 mb-2 text-sm line-clamp-2">{task.task_description}</p>
                                      {task.tags && task.tags.length > 0 &&
                                  <div className="flex gap-1 mb-2 overflow-x-auto">
                                          {task.tags.map((tag, idx) => {
                                      const match = tag.match(/\(([^)]+)\)$/);
                                      const tagType = match ? match[1] : '';
                                      return (
                                        <Badge
                                          key={idx}
                                          variant="outline"
                                          title={tag}
                                          className={`text-[10px] py-0 px-1.5 h-4 whitespace-nowrap max-w-[80px] truncate ${
                                          tagType === 'Officer' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' :
                                          tagType === 'Artifact' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' :
                                          tagType === 'Ritual' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                                          tagType === 'Policy' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                          'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}`
                                          }>

                                                {tag}
                                              </Badge>);

                                    })}
                                        </div>
                                  }
                                      {task.key_result_focus &&
                                  <div className="flex items-start gap-1 text-xs text-slate-500 mb-1">
                                          <Target className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                          <span className="line-clamp-1">{task.key_result_focus}</span>
                                        </div>
                                  }
                                      {task.activity_id && getActivity(task.activity_id) &&
                                  <div className="text-xs text-slate-500 font-mono">
                                          [{getActivity(task.activity_id).activity_code}] {getActivity(task.activity_id).title}
                                        </div>
                                  }
                                    </CardContent>
                                  </Card>
                              )}
                                {squadTasks.filter(taskMatchesFilters).length === 0 && squadTasks.length === 0 &&
                              <div className="text-xs text-slate-600 italic py-2">No tasks planned</div>
                              }
                                {squadTasks.filter((t) => !taskMatchesFilters(t)).length > 0 &&
                              <Badge variant="outline" className="border-slate-600/30 text-slate-500 mt-2">
                                    {squadTasks.filter((t) => !taskMatchesFilters(t)).length} other {squadTasks.filter((t) => !taskMatchesFilters(t)).length === 1 ? 'task' : 'tasks'}
                                  </Badge>
                              }
                              </div>
                            }
                            </div>);

                      })}
                      </div> : (


                    /* Show task count badge when zoomed out */
                    tasks.length > 0 &&
                    <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">
                          {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                        </Badge>)

                    }
                  </div>
                </div>);

            })}
          </div>
        </div>
      </div>

      {/* Add/Edit Task Dialog */}
      <ConfirmableDialog open={addTaskDialog || editTaskDialog} onOpenChange={(open) => {
        if (!open) {
          setAddTaskDialog(false);
          setEditTaskDialog(false);
          setEditingTask(null);
          setTaskForm({
            activity_id: '',
            task_description: '',
            key_result_focus: '',
            time_allocation_percent: 40,
            task_order: 1,
            status_flag: 'gray',
            tags: [],
            assign_to_email: '',
            is_tentative: false,
            category: null,
            xp_value: 0,
            ep_value: 0,
            skill_rewards: [],
            bot_suggested_category: null,
            bot_suggested_xp: null,
            bot_suggested_ep: null,
            bot_suggested_skills: null,
            bot_suggestion_approved: false
          });
          setCurrentTagType('');
          setCurrentTagValue('');
          setAiAssessment(null);
          setDialogMissionFilter([]);
          setDialogObjectiveFilter([]);
        }
      }}>
        <DialogContent data-testid="dash-dailytaskplanner-dialog" className="bg-slate-900 text-white border-slate-800 max-w-2xl max-h-[95vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-16px)] sm:w-full p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              {editingTask ? 'Edit Task' : `Add Task for ${selectedDate && format(selectedDate, 'MMM d, yyyy')}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 min-w-0">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Task Order & Time Allocation</label>
              {(() => {
                const targetOfficer = officers.find(o => o.user_email === (taskForm.assign_to_email || user?.email));
                const isAI = targetOfficer?.custom_role === 'AI Officer';
                
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={taskForm.task_order.toString()}
                      onValueChange={(value) => setTaskForm({ ...taskForm, task_order: parseInt(value) })}>
                      <SelectTrigger className="w-32 bg-slate-950 border-slate-700 text-slate-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Task #1</SelectItem>
                        <SelectItem value="2">Task #2</SelectItem>
                        <SelectItem value="3">Task #3</SelectItem>
                        <SelectItem value="4">Task #4 (Additional)</SelectItem>
                        <SelectItem value="5">Task #5 (Additional)</SelectItem>
                        <SelectItem value="6">Task #6 (Additional)</SelectItem>
                        <SelectItem value="7">Task #7 (Additional)</SelectItem>
                        <SelectItem value="8">Task #8 (Additional)</SelectItem>
                        <SelectItem value="9">Task #9 (Additional)</SelectItem>
                        <SelectItem value="10">Task #10 (Additional)</SelectItem>
                      </SelectContent>
                    </Select>
                    {!isAI && (
                      <>
                        <Input data-testid="dash-dailytaskplanner-input"
                          type="number"
                          value={taskForm.time_allocation_percent}
                          onChange={(e) => setTaskForm({ ...taskForm, time_allocation_percent: parseInt(e.target.value) })}
                          className="w-24 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                          min={taskForm.task_order === 1 ? 40 : taskForm.task_order === 2 ? 30 : 0}
                          max={taskForm.task_order === 1 ? 100 : taskForm.task_order === 2 ? 60 : 100} />

                        <span className="text-sm text-slate-400">
                          % of day ({taskForm.task_order === 1 ? '40-100%' : taskForm.task_order === 2 ? '30-60%' : taskForm.task_order === 3 ? 'remainder' : 'flexible'})
                        </span>
                      </>
                    )}
                    {isAI && (
                      <span className="text-sm text-purple-400">
                        Time allocation doesn't apply to AI Officers - only task order matters
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="min-w-0">
              <label className="text-sm text-slate-400 mb-2 block">Filter by Mission</label>
              <SearchableTagSelect
                options={missions.map(m => ({
                  value: m.id,
                  label: `${m.mission_code ? `[${m.mission_code}] ` : ''}${m.title}`,
                  isMy: m.owner_email === user?.email || m.assigned_to_email === user?.email
                }))}
                value={dialogMissionFilter[0] || ''}
                onValueChange={(value) => setDialogMissionFilter([value])}
                placeholder="All missions"
                emptyMessage="No missions found"
                className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
                renderItem={(option) => (
                  <span className="flex items-center">
                    {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                    {option.label}
                  </span>
                )}
              />
            </div>

            <div className="min-w-0">
              <label className="text-sm text-slate-400 mb-2 block">Filter by Objective</label>
              <SearchableTagSelect
                options={objectives
                  .filter(o => dialogMissionFilter.length === 0 || dialogMissionFilter.includes(o.mission_id))
                  .map(o => ({
                    value: o.id,
                    label: `${o.objective_code ? `[${o.objective_code}] ` : ''}${o.statement}`,
                    isMy: o.owner_email === user?.email
                  }))}
                value={dialogObjectiveFilter[0] || ''}
                onValueChange={(value) => setDialogObjectiveFilter([value])}
                placeholder="All objectives"
                emptyMessage="No objectives found"
                className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
                renderItem={(option) => (
                  <span className="flex items-center">
                    {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                    {option.label}
                  </span>
                )}
              />
            </div>

            <div className="min-w-0">
              <label className="text-sm text-slate-400 mb-2 block">Link to Activity *</label>
              <SearchableTagSelect
                options={allActivities
                  .filter(a => {
                    if (!(a.status === 'active' || (editingTask && a.id === editingTask.activity_id)) || !a.id) return false;
                    
                    // Apply mission filter
                    if (dialogMissionFilter.length > 0) {
                      const objective = objectives.find(o => o.id === a.objective_id);
                      if (!objective || !dialogMissionFilter.includes(objective.mission_id)) return false;
                    }
                    
                    // Apply objective filter
                    if (dialogObjectiveFilter.length > 0) {
                      if (!dialogObjectiveFilter.includes(a.objective_id)) return false;
                    }
                    
                    // For bot activities: only show if current user is the bot owner
                    const targetOfficer = taskForm.assign_to_email || user?.email;
                    const botOfficer = officers.find(o => o.user_email === targetOfficer);
                    if (botOfficer?.is_bot) {
                      if (botOfficer.bot_owner_email !== user?.email) {
                        return false;
                      }
                    }
                    
                    return true;
                  })
                  .map((activity) => {
                    const responsibleOfficer = officers.find(o => o.user_email === activity.responsible_email);
                    const isAI = responsibleOfficer?.custom_role === 'AI Officer';
                    const isMyActivity = activity.accountable_email === user?.email || activity.responsible_email === user?.email;
                    return {
                      value: activity.id,
                      label: `${activity.status === 'archived' ? '[archived!] ' : ''}[${activity.activity_code}] ${activity.title}`,
                      isMy: isMyActivity,
                      isAI: isAI,
                      isArchived: activity.status === 'archived'
                    };
                  })}
                value={taskForm.activity_id}
                renderItem={(option) => (
                  <span className="flex items-center">
                    {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                    {option.isAI && <span className="text-purple-400 mr-1">🤖</span>}
                    {option.label}
                  </span>
                )}
                onValueChange={(v) => {
                  const selectedActivity = allActivities.find(a => a.id === v);
                  const responsibleOfficer = officers.find(o => o.user_email === selectedActivity?.responsible_email);
                  const isAIResponsible = responsibleOfficer?.custom_role === 'AI Officer';
                  
                  // Preserve pre-set assignment (e.g. from "Add task for squad member/bot" button)
                  if (taskForm.assign_to_email) {
                    setTaskForm({ ...taskForm, activity_id: v });
                    return;
                  }
                  
                  // Check if user is responsible or accountable for this activity
                  const isResponsibleOrAccountable = selectedActivity?.responsible_email === user?.email || selectedActivity?.accountable_email === user?.email;

                  let newAssignToEmail = '';
                  let newIsTentative = false;

                  // If user is NOT responsible/accountable, mark as tentative (stays with current user, needs approval)
                  if (!isResponsibleOrAccountable) {
                    newAssignToEmail = '';  // Keep with current user
                    newIsTentative = true;  // Mark as tentative for approval
                  }
                  // If responsible is AI and user is accountable, assign to AI
                  else if (isAIResponsible && selectedActivity?.accountable_email === user?.email) {
                    newAssignToEmail = selectedActivity.responsible_email;
                    newIsTentative = false;
                  }
                  // Otherwise, normal task for current user
                  else {
                    newAssignToEmail = '';
                    newIsTentative = false;
                  }
                  
                  setTaskForm({ ...taskForm, activity_id: v, assign_to_email: newAssignToEmail, is_tentative: newIsTentative });
                }}
                placeholder="Select an activity..."
                emptyMessage="No activities found"
                className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
              />
            </div>

            {/* Show info if tentative task or AI assignment */}
            {taskForm.is_tentative && !taskForm.assign_to_email && (
              <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
                <div className="flex items-center gap-2 text-sm text-amber-300">
                  <AlertTriangle className="w-4 h-4" />
                  <span>
                    Creating tentative task (requires approval from {(() => {
                      const activity = allActivities.find(a => a.id === taskForm.activity_id);
                      const approverEmail = activity?.accountable_email || activity?.responsible_email;
                      const approver = officers.find(o => o.user_email === approverEmail);
                      return approver?.callsign || approverEmail || 'activity owner';
                    })()})
                  </span>
                </div>
              </div>
            )}
            {taskForm.assign_to_email && !taskForm.is_tentative && (
              <div className="p-3 rounded-lg border bg-indigo-500/10 border-indigo-500/30">
                <div className="flex items-center gap-2 text-sm text-indigo-300">
                  <Bot className="w-4 h-4" />
                  <span>
                    Creating task for AI Officer: {officers.find(o => o.user_email === taskForm.assign_to_email)?.callsign}
                  </span>
                </div>
              </div>
            )}
            {taskForm.assign_to_email && taskForm.is_tentative && (
              <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
                <div className="flex items-center gap-2 text-sm text-amber-300">
                  <AlertTriangle className="w-4 h-4" />
                  <span>
                    Creating tentative task for {officers.find(o => o.user_email === taskForm.assign_to_email)?.callsign} (requires their acceptance)
                  </span>
                </div>
              </div>
            )}

            {selectedActivity?.key_results && selectedActivity.key_results.length > 0 &&
            <div className="min-w-0">
                <label className="text-sm text-slate-400 mb-2 block">Key Result Focus</label>
                <Select value={taskForm.key_result_focus} onValueChange={(v) => setTaskForm({ ...taskForm, key_result_focus: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 max-w-full [&>span]:truncate [&>span]:max-w-[calc(100%-24px)]">
                    <SelectValue placeholder="Select key result to advance..." />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-32px)]">
                    {selectedActivity.key_results.filter(kr => kr.description).map((kr, idx) =>
                  <SelectItem key={idx} value={kr.description} className="max-w-full">
                        <span className="line-clamp-2">{kr.description} ({kr.current_value}/{kr.target} {kr.unit})</span>
                      </SelectItem>
                  )}
                  </SelectContent>
                </Select>
              </div>
            }

            <div>
              <label className="text-sm text-slate-400 mb-2 block">Task Description</label>
              <Textarea data-testid="dash-dailytaskplanner-textarea"
                value={taskForm.task_description}
                onChange={(e) => setTaskForm({ ...taskForm, task_description: e.target.value })}
                placeholder="What will you accomplish today?"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[100px] mb-2" />
              
              <div className="flex flex-col gap-2">
                <Button 
                  type="button" 
                  onClick={async () => {
                    if (!taskForm.activity_id) {
                      alert("Please select an activity first.");
                      return;
                    }
                    setIsGeneratingTask(true);
                    setAiAssessment(null);
                    try {
                      const res = await base44.functions.invoke('generateTasks', { activity_id: taskForm.activity_id, mode: 'generate', target: 'human' });
                      if (res.data && res.data.suggested_task) {
                        setTaskForm(prev => ({ ...prev, task_description: res.data.suggested_task }));
                        setAiAssessment(res.data.assessment);
                      }
                    } catch (err) {
                      console.error("Failed to generate task", err);
                      alert("Failed to generate task. Please try again.");
                    } finally {
                      setIsGeneratingTask(false);
                    }
                  }}
                  disabled={isGeneratingTask || !taskForm.activity_id}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {isGeneratingTask ? 'Generating...' : 'AI Generate Task'}
                </Button>

                {aiAssessment && aiAssessment.type !== 'ok' && (
                  <div className={`text-sm p-3 rounded-md flex items-start gap-2 ${
                    aiAssessment.type === 'redundant' ? 'bg-red-500/10 border border-red-500/30 text-red-200' : 
                    'bg-yellow-500/10 border border-yellow-500/30 text-yellow-200'
                  }`}>
                    {aiAssessment.type === 'redundant' ? 
                      <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0 text-red-400" /> : 
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" />
                    }
                    <div className="flex-1">
                      <span className="font-semibold block mb-1">
                        {aiAssessment.type === 'redundant' ? 'Redundant Task Detected' : 'Potential Overlap Detected'}
                      </span>
                      {aiAssessment.message}
                      {aiAssessment.linked_task_id && (
                        <div className="mt-1">
                          {/* We can't easily link to the task if it's not loaded in the view, but we can mention it */}
                          <span className="text-xs opacity-70">Refers to existing task.</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Gamification Points Panel */}
            <TaskCategorizationPanel
              taskDescription={taskForm.task_description}
              keyResultFocus={taskForm.key_result_focus}
              activityId={taskForm.activity_id}
              onCategorize={(gamificationData) => {
                setTaskForm(prev => ({
                  ...prev,
                  ...gamificationData
                }));
              }}
              initialValues={taskForm.category ? {
                category: taskForm.category,
                xp_value: taskForm.xp_value,
                ep_value: taskForm.ep_value,
                skill_rewards: taskForm.skill_rewards
              } : null}
            />

            <div>
              <label className="text-sm text-slate-400 mb-2 block">Tags</label>
              <div className="flex flex-wrap gap-2 p-3 bg-slate-950 border border-slate-700 rounded-lg min-h-[42px] mb-2">
                {taskForm.tags.map((tag, idx) => {
                  const match = tag.match(/\(([^)]+)\)$/);
                  const tagType = match ? match[1] : '';
                  return (
                    <Badge
                      key={idx}
                      variant="outline"
                      className={`text-xs ${
                      tagType === 'Officer' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30' :
                      tagType === 'Artifact' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' :
                      tagType === 'Ritual' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' :
                      tagType === 'Policy' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                      'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'}`
                      }>

                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-red-400">

                        <X className="w-3 h-3" />
                      </button>
                    </Badge>);

                })}
              </div>
              <div className="flex flex-wrap gap-2">
                <Select
                  value={currentTagType}
                  onValueChange={setCurrentTagType}>

                  <SelectTrigger className="bg-slate-950 border-slate-700 w-28 sm:w-32 flex-shrink-0">
                    <SelectValue placeholder="Type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Officer">Officer</SelectItem>
                    <SelectItem value="Artifact">Artifact</SelectItem>
                    <SelectItem value="Ritual">Ritual</SelectItem>
                    <SelectItem value="Policy">Policy</SelectItem>
                    <SelectItem value="Capability">Capability</SelectItem>
                  </SelectContent>
                </Select>

                {currentTagType === 'Officer' ?
                <SearchableTagSelect
                  options={officers.map(o => ({ value: o.user_email, label: o.callsign }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select officer..."
                  emptyMessage="No officers found."
                  className="flex-1"
                /> :
                currentTagType === 'Artifact' ?
                <SearchableTagSelect
                  options={artifacts.filter(a => {
                    // Filter company secrets
                    if (a.is_company_secret) {
                      if (a.owner_email === user?.email) return true;
                      if ((a.editors || []).includes(user?.email)) return true;
                      if ((a.viewers || []).includes(user?.email)) return true;
                      return false;
                    }
                    return true;
                  }).map(a => ({ value: a.name, label: a.name }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select artifact..."
                  emptyMessage="No artifacts found."
                  className="flex-1"
                /> :
                currentTagType === 'Ritual' ?
                <SearchableTagSelect
                  options={rituals.filter(r => {
                    // Filter company secrets
                    if (r.is_company_secret) {
                      if (r.owner_email === user?.email) return true;
                      if ((r.participants || []).includes(user?.email)) return true;
                      return false;
                    }
                    return true;
                  }).map(r => ({ value: r.name, label: r.name }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select ritual..."
                  emptyMessage="No rituals found."
                  className="flex-1"
                /> :
                currentTagType === 'Policy' ?
                <SearchableTagSelect
                  options={policies.map(p => ({ value: p.title, label: p.title }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select policy..."
                  emptyMessage="No policies found."
                  className="flex-1"
                /> :
                currentTagType === 'Capability' ?
                <SearchableTagSelect
                  options={capabilities.map(c => ({ value: c.name, label: c.name }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select capability..."
                  emptyMessage="No capabilities found."
                  className="flex-1"
                /> :
                currentTagType ?
                <Input data-testid="dash-dailytaskplanner-input-1"
                  placeholder={`Enter ${currentTagType.toLowerCase()} name...`}
                  value={currentTagValue}
                  onChange={(e) => setCurrentTagValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 flex-1" /> :


                <Input data-testid="dash-dailytaskplanner-input-2"
                  placeholder="Select type first..."
                  disabled
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 flex-1" />

                }

                <Button
                  type="button"
                  onClick={addTag}
                  disabled={!currentTagType || !currentTagValue}
                  className="bg-indigo-600 hover:bg-indigo-700">

                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {editingTask && (
              <div className="pt-4 border-t border-slate-700">
                <div className="text-xs text-slate-400 mb-2">Submit Artifact</div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input data-testid="dash-dailytaskplanner-input-3"
                      type="file"
                      id="edit-artifact-upload"
                      className="hidden"
                      onChange={(e) => handleArtifactUpload(e, editingTask)}
                      disabled={uploadingArtifact}
                    />
                    <label
                      htmlFor="edit-artifact-upload"
                      className={`flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm cursor-pointer transition-colors ${uploadingArtifact ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingArtifact ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload & Submit for Approval
                        </>
                      )}
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    Uploads a file and creates a pending artifact linked to this task.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-slate-700">
              <Button variant="outline" onClick={() => {
                setAddTaskDialog(false);
                setEditTaskDialog(false);
                setEditingTask(null);
              }} className="text-slate-900 w-full sm:w-auto">
                Cancel
              </Button>
              <Button onClick={handleSubmitTask} className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto">
                {editingTask ? 'Update Task' : 'Add Task'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </ConfirmableDialog>

      {/* Commander Validation Dialog */}
      <DailyTaskPrinciplesValidation
        tasks={validatingTasks}
        open={validatingTasks.length > 0}
        onClose={() => setValidatingTasks([])}
        currentUser={user}
        officers={officers}
      />

      {/* Execution Progress Dialog */}
      <Dialog open={executionDialog.open} onOpenChange={(open) => !open && setExecutionDialog({ open: false, taskId: null, progress: 0, completed: false })}>
        <DialogContent data-testid="dash-dailytaskplanner-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-400" />
              AI Officer Executing Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3">
              {!executionDialog.completed ? (
                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
              ) : (
                <Check className="w-5 h-5 text-emerald-400" />
              )}
              <span className="text-sm text-slate-300">
                {executionDialog.completed ? 'Execution completed!' : 'Analyzing task and generating response...'}
              </span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${executionDialog.progress}%` }}
              />
            </div>
            <div className="text-xs text-slate-500 text-right">
              {Math.round(executionDialog.progress)}%
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Artifact Approval Dialog */}
      <Dialog open={artifactApprovalDialog.open} onOpenChange={(open) => !open && setArtifactApprovalDialog({ open: false, artifacts: [], uploadedFiles: [], taskUserEmail: null })}>
        <DialogContent data-testid="dash-dailytaskplanner-dialog-2" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Create Artifacts?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              The AI Officer suggests creating the following artifacts from the generated files:
            </p>
            <div className="space-y-3">
              {artifactApprovalDialog.artifacts.map((artifact, idx) => (
                <div key={idx} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-slate-200 mb-1">{artifact.name}</div>
                      <div className="text-xs text-slate-400 mb-2">{artifact.description}</div>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="text-xs">{artifact.category}</Badge>
                        <Badge variant="outline" className="text-xs">{artifact.type}</Badge>
                        {artifact.linked_filename && (
                          <Badge variant="outline" className="text-xs text-cyan-400 border-cyan-500/30">
                            📎 {artifact.linked_filename}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button
                variant="outline"
                onClick={() => setArtifactApprovalDialog({ open: false, artifacts: [], uploadedFiles: [], taskUserEmail: null })}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                No, Skip Artifacts
              </Button>
              <Button
                onClick={async () => {
                  try {
                    for (const artifact of artifactApprovalDialog.artifacts) {
                      let fileUrl = null;
                      
                      if (artifact.type === 'uploaded' && artifact.linked_filename) {
                        const linkedFile = artifactApprovalDialog.uploadedFiles.find(f => f.filename === artifact.linked_filename);
                        if (linkedFile) {
                          fileUrl = linkedFile.url;
                        }
                      } else if (artifact.type === 'link') {
                        fileUrl = artifact.file_url;
                      }
                      
                      await base44.entities.Artifact.create({
                        name: artifact.name,
                        description: artifact.description,
                        category: artifact.category,
                        type: artifact.type,
                        file_url: fileUrl,
                        internal_page: artifact.type === 'internal' ? artifact.internal_page : null,
                        owner_email: artifactApprovalDialog.taskUserEmail,
                        tags: artifact.tags || [],
                        status: 'draft'
                      });
                    }
                    
                    setArtifactApprovalDialog({ open: false, artifacts: [], uploadedFiles: [], taskUserEmail: null });
                    alert(`Successfully created ${artifactApprovalDialog.artifacts.length} artifact(s)!`);
                  } catch (error) {
                    alert(`Failed to create artifacts: ${error.message}`);
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Yes, Create All Artifacts
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={taskDetailDialog.open} onOpenChange={(open) => !open && setTaskDetailDialog({ open: false, task: null })}>
        <DialogContent data-testid="dash-dailytaskplanner-dialog-3" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Task Details
              {taskDetailDialog.task?.generated_by_ai && <Bot className="w-4 h-4 text-purple-400" />}
            </DialogTitle>
          </DialogHeader>
          {taskDetailDialog.task && (
            <div className="space-y-4 mt-4">
              <div>
                <div className="text-xs text-slate-500 mb-1">Description</div>
                <p className="text-slate-200">{taskDetailDialog.task.task_description}</p>
              </div>

              {taskDetailDialog.task.key_result_focus && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Key Result Focus</div>
                  <p className="text-slate-300">{taskDetailDialog.task.key_result_focus}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Time Allocation</div>
                  <Badge variant="outline" className="bg-slate-800/50 text-slate-300 border-slate-700">
                    {taskDetailDialog.task.time_allocation_percent}%
                  </Badge>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Status Flag</div>
                  <Badge className={
                    taskDetailDialog.task.status_flag === 'green' ? 'bg-emerald-500/20 text-emerald-400' :
                    taskDetailDialog.task.status_flag === 'yellow' ? 'bg-amber-500/20 text-amber-400' :
                    taskDetailDialog.task.status_flag === 'red' ? 'bg-red-500/20 text-red-400' :
                    'bg-slate-500/20 text-slate-400'
                  }>
                    {taskDetailDialog.task.status_flag?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              {taskDetailDialog.task.uploaded_files_meta && taskDetailDialog.task.uploaded_files_meta.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Generated Files</div>
                  <div className="flex flex-wrap gap-2">
                    {taskDetailDialog.task.uploaded_files_meta.map((file, idx) => (
                      <a 
                        key={idx} 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-sm hover:bg-cyan-500/20 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {file.filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {taskDetailDialog.task.bot_execution_result && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-semibold text-purple-300">AI Execution Report</span>
                  </div>
                  <div className="text-sm text-slate-300 leading-relaxed bg-purple-500/5 p-4 rounded border border-purple-500/20 prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown>{taskDetailDialog.task.bot_execution_result}</ReactMarkdown>
                  </div>
                </div>
              )}

              {/* Parent Hierarchy: Activity → Objective → Mission */}
              {taskDetailDialog.task.activity_id && (() => {
                const activity = allActivities.find(a => a.id === taskDetailDialog.task.activity_id);
                if (!activity) return null;
                
                const objective = objectives.find(o => o.id === activity.objective_id);
                const mission = objective ? missions.find(m => m.id === objective.mission_id) : null;
                
                return (
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
                    <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">Parent Hierarchy</div>
                    
                    {/* Mission */}
                    {mission && (
                      <div className="flex items-start gap-2">
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs flex-shrink-0">Mission</Badge>
                        <Link 
                          to={createPageUrl('missions')}
                          className="text-sm text-slate-300 hover:text-indigo-400 transition-colors flex items-center gap-1"
                          onClick={() => setTaskDetailDialog({ open: false, task: null })}
                        >
                          [{mission.mission_code}] {mission.title}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    )}
                    
                    {/* Objective */}
                    {objective && (
                      <div className="flex items-start gap-2 ml-4">
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs flex-shrink-0">Objective</Badge>
                        <Link 
                          to={createPageUrl('discovery') + `?mission=${objective.mission_id}`}
                          className="text-sm text-slate-300 hover:text-indigo-400 transition-colors flex items-center gap-1"
                          onClick={() => setTaskDetailDialog({ open: false, task: null })}
                        >
                          [{objective.objective_code}] {objective.statement}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    )}
                    
                    {/* Activity */}
                    <div className="flex items-start gap-2 ml-8">
                      <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-xs flex-shrink-0">Activity</Badge>
                      <Link 
                        to={createPageUrl('activities')}
                        className="text-sm text-slate-300 hover:text-indigo-400 transition-colors flex items-center gap-1"
                        onClick={() => setTaskDetailDialog({ open: false, task: null })}
                      >
                        [{activity.activity_code}] {activity.title}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                );
              })()}

              {taskDetailDialog.task.tags && taskDetailDialog.task.tags.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {taskDetailDialog.task.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs text-slate-300 border-slate-600">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-800">
                <div className="text-xs text-slate-500 mb-2">Submit Artifact</div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input data-testid="dash-dailytaskplanner-input-4"
                      type="file"
                      id="artifact-upload"
                      className="hidden"
                      onChange={(e) => handleArtifactUpload(e, taskDetailDialog.task)}
                      disabled={uploadingArtifact}
                    />
                    <label
                      htmlFor="artifact-upload"
                      className={`flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm cursor-pointer transition-colors ${uploadingArtifact ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingArtifact ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload & Submit for Approval
                        </>
                      )}
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    Uploads a file and creates a pending artifact linked to this task.
                  </p>
                </div>
              </div>

            </div>
          )}
        </DialogContent>
      </Dialog>

      <MicroTaskListDialog
        open={microTaskListDialog.open}
        onOpenChange={(open) => setMicroTaskListDialog(prev => ({ ...prev, open }))}
        officer={microTaskListDialog.officer}
        currentUser={user}
      />

      {/* Bot Plan Dialog */}
      <BotPlanDialog
        open={botPlanDialog.open}
        onOpenChange={(open) => setBotPlanDialog(prev => ({ ...prev, open }))}
        task={botPlanDialog.task}
        onApprove={(additionalContext) => {
          const task = botPlanDialog.task;
          if (task) {
            executeBotTask(task, additionalContext);
          }
        }}
      />

      {/* Document Review Dialog - user reviews before saving */}
      <BotDocumentReviewDialog
        open={documentReviewDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDocumentReviewDialog({ open: false, documentOutput: null, taskId: null, taskUserEmail: null });
          }
        }}
        documentOutput={documentReviewDialog.documentOutput}
        taskId={documentReviewDialog.taskId}
        onApprove={(approvedDoc) => {
          const taskUserEmail = documentReviewDialog.taskUserEmail;
          setDocumentReviewDialog({ open: false, documentOutput: null, taskId: null, taskUserEmail: null });
          // Small delay to let first dialog close before opening second
          setTimeout(() => {
            setDocumentSaveDialog({ open: true, documentOutput: approvedDoc, taskUserEmail });
          }, 150);
        }}
        onCancel={() => {
          setDocumentReviewDialog({ open: false, documentOutput: null, taskId: null, taskUserEmail: null });
        }}
      />

      {/* Document Save Dialog - for bot execution */}
      <DocumentSaveDialog
        open={documentSaveDialog.open}
        onOpenChange={(open) => !open && setDocumentSaveDialog({ open: false, documentOutput: null, taskUserEmail: null })}
        documentOutput={documentSaveDialog.documentOutput}
        onComplete={() => {
          setDocumentSaveDialog({ open: false, documentOutput: null, taskUserEmail: null });
          queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          queryClient.invalidateQueries({ queryKey: ['artifacts'] });
        }}
      />

      {/* Archive Confirmation Dialog */}
      <ArchiveConfirmDialog
        open={archiveDialog.open}
        onOpenChange={(open) => !open && setArchiveDialog({ open: false, task: null })}
        onConfirm={async () => {
          if (archiveDialog.task) {
            await updateTaskMutation.mutateAsync({
              id: archiveDialog.task.id,
              data: {
                ...archiveDialog.task,
                archived: true,
                archived_at: new Date().toISOString()
              }
            });
            setArchiveDialog({ open: false, task: null });
          }
        }}
        itemType="task"
        itemTitle={archiveDialog.task?.task_description}
      />

      {/* Issue Report Dialog (triggered when red flag is selected) */}
      {issueReportDialog.task && (() => {
        const activity = allActivities.find(a => a.id === issueReportDialog.task?.activity_id);
        if (!activity) return null;
        
        return (
          <ReportActivityIssueModal
            activity={activity}
            open={issueReportDialog.open}
            onClose={() => setIssueReportDialog({ open: false, task: null })}
            officers={officers}
            artifacts={artifacts}
            rituals={rituals}
            policies={policies}
            capabilities={capabilities}
            initialData={{
              title: `Struggling: ${issueReportDialog.task?.task_description || ''}`,
              description: '',
              source: 'task',
              source_id: issueReportDialog.task?.id
            }}
          />
        );
      })()}

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectionDialog.open} onOpenChange={(open) => !open && setRejectionDialog({ open: false, task: null, reason: '' })}>
        <DialogContent data-testid="dash-dailytaskplanner-dialog-4" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <X className="w-5 h-5" />
              Reject Task{rejectionDialog.isBulk ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Please provide a reason for rejecting {rejectionDialog.isBulk ? 'these tasks' : 'this task'}. 
              This will help {(() => {
                const task = Array.isArray(rejectionDialog.task) ? rejectionDialog.task[0] : rejectionDialog.task;
                const creator = officers.find(o => o.user_email === task?.created_by_email);
                return creator?.callsign || 'the assigning officer';
              })()} understand why.
            </p>
            <Textarea data-testid="dash-dailytaskplanner-textarea-1"
              value={rejectionDialog.reason}
              onChange={(e) => setRejectionDialog(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g., I'm already at capacity, this conflicts with another priority, I don't have the required skills for this..."
              className="bg-slate-950 border-slate-700 text-slate-100 min-h-[100px]"
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setRejectionDialog({ open: false, task: null, reason: '' })}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const tasks = Array.isArray(rejectionDialog.task) ? rejectionDialog.task : [rejectionDialog.task];
                  const rejectionReason = rejectionDialog.reason || 'No reason provided';
                  
                  await Promise.all(tasks.map(async (t) => {
                    const updatedTask = { 
                      ...t, 
                      status: 'rejected',
                      rejection_reason: rejectionReason
                    };
                    await base44.entities.DailyTask.update(t.id, updatedTask);
                    
                    // Send notification to the creator if they exist
                    if (t.created_by_email && t.created_by_email !== user?.email) {
                      try {
                        await base44.functions.invoke('sendNotification', {
                          recipient_email: t.created_by_email,
                          type: 'task_rejected',
                          source_type: 'daily_task',
                          source_id: t.id,
                          source_code: t.activity_id ? allActivities?.find(a => a.id === t.activity_id)?.activity_code : null,
                          source_name: t.task_description,
                          triggered_by_email: user?.email,
                          deep_link_params: {
                            date: t.date,
                            user_email: t.user_email,
                            task_id: t.id
                          },
                          metadata: {
                            task_description: t.task_description,
                            activity_code: allActivities?.find(a => a.id === t.activity_id)?.activity_code,
                            rejection_reason: rejectionReason
                          }
                        });
                      } catch (err) {
                        console.error('Failed to send rejection notification:', err);
                      }
                    }
                  }));
                  
                  queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
                  setRejectionDialog({ open: false, task: null, reason: '' });
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Reject {rejectionDialog.isBulk ? 'All Tasks' : 'Task'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>);

}